import * as PrestigeSystem from '../game/PrestigeSystem.js';
import { UIParticleSystem } from './UIParticleSystem.js';
import { PopupManager } from './PopupManager.js';
import { ModalManager } from './ModalManager.js';
import { UpgradeMenuManager } from './UpgradeMenuManager.js';
import * as StatsFormatter from './StatsFormatter.js';
import { showInGameAccountModal, showLeaderboardModal } from './FirebaseUI.js';
import { showBugReportModal } from './BugReportUI.js';
import { getUIUpdateScheduler } from '../systems/index.js';
import './gameui.css';

// Game UI management
export class GameUI {
    constructor(gameState, driveStateMachine, musicPlayer, sfxManager = null, app = null) {
        this.gameState = gameState;
        this.driveStateMachine = driveStateMachine;
        this.musicPlayer = musicPlayer;
        this.sfxManager = sfxManager;
        this.app = app;
        this.elements = {};

        // Cache for preventing redundant updates
        this.lastState = null;
        this.lastMiles = undefined; // Use undefined to detect first update
        this.lastSpeed = -1;
        this.lastCountdownSeconds = -1;
        this.lastCruiseButtonText = '';
        this.maxSpeedEffectsActive = false; // Prevent FOV stacking
        this.hasTriggeredMaxThisSession = false; // Track if we've triggered max effects this speed session
        this.momentumLossAnimationActive = false; // Track if momentum loss animation is playing
        this.momentumDisplayWasVisible = false; // Track if momentum display was visible last frame

        // Initialize particle system
        this.particles = new UIParticleSystem(this);

        // Initialize popup manager
        this.popups = new PopupManager();

        // Initialize modal manager
        this.modals = new ModalManager();

        // Initialize upgrade menu manager
        this.upgradeMenu = new UpgradeMenuManager(this, gameState);

        // Load upgrade sound (with cache busting)
        const cacheBuster = `?t=${Date.now()}`;
        this.upgradeSound = new Audio(`./assets/sfx/upgrade.mp3${cacheBuster}`);
        this.upgradeSound.volume = 0.5;

        // Load level available sound
        this.levelAvailSound = new Audio(`./assets/sfx/levelAvail.mp3${cacheBuster}`);
        this.levelAvailSound.volume = 0.4;

        // Load slowdown sound
        this.slowdownSound = new Audio(`./assets/sfx/slowdown.mp3${cacheBuster}`);
        this.slowdownSound.volume = 0.5;

        // Register sounds with SFXManager when it becomes available
        this._registerSoundsWithSFXManager();

        // Track initial badge state (to prevent confetti if already visible at load)
        this.initialBadgeCheckDone = false;

        // Mobile detection
        this.isMobile = this.detectMobile();

        // Track music modal state
        this.musicModalOpen = false;

        this.create();

        // Set up global tooltip dismiss listeners
        this.setupTooltipDismissListeners();
    }

    detectMobile() {
        // Check for touch support and small screen size
        const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
        const isSmallScreen = window.innerWidth <= 768 || window.innerHeight <= 768;
        return hasTouch && isSmallScreen;
    }

    /**
     * Detect if device is a phone (not a tablet like iPad)
     * Phone: touch device with smaller screen dimension ≤480px
     * iPad Mini is ~768px, phones are typically ≤430px width in portrait
     */
    isPhoneScreen() {
        const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
        const isPhone = Math.min(window.innerWidth, window.innerHeight) <= 480;
        return hasTouch && isPhone;
    }

    /**
     * Check if device is in landscape orientation
     */
    isLandscape() {
        return window.innerWidth > window.innerHeight;
    }

    /**
     * Position a tooltip safely within viewport bounds
     * @param {HTMLElement} tooltip - The tooltip element
     * @param {HTMLElement} anchor - The anchor element (icon) to position relative to
     * @param {object} options - Positioning options
     */
    positionTooltip(tooltip, anchor, options = {}) {
        const padding = options.padding ?? 8; // Minimum distance from viewport edge
        const gap = options.gap ?? 8; // Gap between anchor and tooltip

        const anchorRect = anchor.getBoundingClientRect();
        const tooltipRect = tooltip.getBoundingClientRect();

        // Start with centered above the anchor
        let left = anchorRect.left + anchorRect.width / 2 - tooltipRect.width / 2;
        let top = anchorRect.top - tooltipRect.height - gap;

        // Clamp horizontal position to viewport
        const maxLeft = window.innerWidth - tooltipRect.width - padding;
        const minLeft = padding;
        left = Math.max(minLeft, Math.min(maxLeft, left));

        // If tooltip would go above viewport, show it below instead
        if (top < padding) {
            top = anchorRect.bottom + gap;
        }

        // Clamp vertical position to viewport (in case it's really tall)
        const maxTop = window.innerHeight - tooltipRect.height - padding;
        top = Math.max(padding, Math.min(maxTop, top));

        tooltip.style.left = `${left}px`;
        tooltip.style.top = `${top}px`;
    }

    /**
     * Dismiss all visible tooltips
     */
    dismissAllTooltips() {
        document.querySelectorAll('.ascension-tooltip, .upgrade-tooltip, .cf-item-tooltip').forEach(t => {
            t.style.display = 'none';
        });
    }

    /**
     * Set up global tooltip dismiss listeners (call once during init)
     */
    setupTooltipDismissListeners() {
        // Dismiss on click/tap outside tooltips
        document.addEventListener('click', (e) => {
            // Don't dismiss if clicking on an info icon (let the icon's handler toggle it)
            if (e.target.textContent === 'ⓘ') return;
            // Don't dismiss if clicking on item upgrade button (it shows the tooltip)
            if (e.target.dataset?.buttonType === 'itemUpgrade') return;
            // Don't dismiss if clicking inside a tooltip
            if (e.target.closest('.cf-item-tooltip')) return;
            this.dismissAllTooltips();
        }, true);

        // Dismiss on scroll (use capture to catch scroll on any element)
        document.addEventListener('scroll', () => {
            this.dismissAllTooltips();
        }, true);
    }

    create() {
        // Create UI container
        const uiContainer = document.createElement('div');
        uiContainer.id = 'game-ui';
        uiContainer.style.cssText = `
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            pointer-events: none;
            font-family: 'Courier New', monospace;
            color: rgba(255, 255, 255, 0.9);
            z-index: 100;
        `;
        document.body.appendChild(uiContainer);

        // Miles counter (above Spend Miles button) - Hidden in compact layout
        const milesDisplay = document.createElement('div');
        milesDisplay.id = 'miles-display';
        milesDisplay.style.cssText = `
            position: absolute;
            bottom: 110px;
            right: 40px;
            font-size: 16px;
            font-weight: normal;
            letter-spacing: 1px;
            background: rgba(15, 15, 25, 0.75);
            padding: 8px 12px;
            border-radius: 6px;
            border: 1px solid rgba(255, 255, 255, 0.12);
            display: none;
            backdrop-filter: blur(10px);
        `;
        milesDisplay.textContent = '0 MI';
        uiContainer.appendChild(milesDisplay);
        this.elements.milesDisplay = milesDisplay;

        // Game clock container (top right) - styled via game-hud.css
        const gameClockContainer = document.createElement('div');
        gameClockContainer.id = 'game-clock-container';
        uiContainer.appendChild(gameClockContainer);

        // Game clock display - styled via game-hud.css
        const gameClockDisplay = document.createElement('div');
        gameClockDisplay.id = 'game-clock-display';
        gameClockDisplay.textContent = '12:00 PM';
        gameClockContainer.appendChild(gameClockDisplay);
        this.elements.gameClockDisplay = gameClockDisplay;

        // Double-tap clock to open debug menu (works on both desktop and mobile)
        this.clockLastTap = 0;
        const handleClockTap = () => {
            const now = Date.now();
            if (now - this.clockLastTap < 300) {
                // Double tap detected
                if (this.app && this.app.debugMenu) {
                    this.app.debugMenu.toggle();
                }
                this.clockLastTap = 0; // Reset to prevent triple-tap
            } else {
                this.clockLastTap = now;
            }
        };
        gameClockDisplay.addEventListener('click', handleClockTap);
        gameClockDisplay.addEventListener('touchend', (e) => {
            e.preventDefault(); // Prevent double-firing with click
            handleClockTap();
        });

        // Time compression badge (hidden by default) - styled via game-hud.css
        const timeCompressionBadge = document.createElement('div');
        timeCompressionBadge.id = 'time-compression-badge';
        gameClockContainer.appendChild(timeCompressionBadge);
        this.elements.timeCompressionBadge = timeCompressionBadge;

        // Speed indicator removed - now shown in drive button

        // Drive button container (bottom center) - styled via game-hud.css
        const driveButtonContainer = document.createElement('div');
        driveButtonContainer.id = 'drive-button-container';
        uiContainer.appendChild(driveButtonContainer);

        // Momentum display - appended to body for fixed positioning
        // Positioned via CSS - centered above drive button on desktop, bottom-left on mobile landscape
        const momentumDisplay = document.createElement('div');
        momentumDisplay.id = 'momentum-display';

        // Progress bar (fills based on momentum / momentum cap)
        const momentumProgressBar = document.createElement('div');
        momentumProgressBar.id = 'momentum-progress-bar';
        momentumDisplay.appendChild(momentumProgressBar);

        // Text container (above the bar)
        const momentumText = document.createElement('span');
        momentumText.id = 'momentum-text';
        momentumDisplay.appendChild(momentumText);

        // Append directly to body for reliable fixed positioning
        document.body.appendChild(momentumDisplay);
        this.elements.momentumDisplay = momentumDisplay;
        this.elements.momentumProgressBar = momentumProgressBar;
        this.elements.momentumText = momentumText;

        // Drive button - styled via game-hud.css
        const driveButton = document.createElement('button');
        driveButton.id = 'drive-button';
        driveButton.textContent = 'DRIVE';
        // Handle drive button action (click or spacebar)
        const handleDrive = (x, y) => {
            // Block input during autopilot
            if (this.driveStateMachine.isCruiseControlActive()) {
                return;
            }

            const currentSpeed = this.driveStateMachine.getSpeed();
            const maxSpeed = this.gameState.getCurrentMaxSpeed();

            // Check if we're about to reach max speed BEFORE clicking
            const wasNotAtMax = currentSpeed < maxSpeed;

            const clickResult = this.driveStateMachine.onDriveButtonClick();

            // Animate momentum display falling if momentum was lost
            if (clickResult.lostMomentum) {
                this.playMomentumLossAnimation();
            }

            // Show +X MPH popup if click was successful
            if (clickResult.success) {
                const accelBoost = this.gameState.getCurrentAcceleration();

                if (clickResult.isOverboost) {
                    // Show red boost popup at click location
                    if (!window._debugDisablePopups) {
                        const overboostAmount = accelBoost * (this.driveStateMachine.isCruiseControlActive() ? 0.25 : 0.5);
                        this.popups.showBoost(x, y, overboostAmount);
                    }

                    // Add shake animation to drive button
                    this.elements.driveButton.classList.add('shake');
                    setTimeout(() => {
                        this.elements.driveButton.classList.remove('shake');
                    }, 200);
                } else {
                    // Show normal green acceleration popup
                    if (!window._debugDisablePopups) {
                        this.popups.showAcceleration(x, y, accelBoost);
                    }
                }

                // Play rev sound tied to successful click (same as popup spawn)
                const newSpeed = this.driveStateMachine.getSpeed();
                const speedPercentage = Math.min(newSpeed / maxSpeed, 1.0);
                if (this.sfxManager && !window._debugDisableRevSound) {
                    this.sfxManager.playRev(speedPercentage);
                }

                // Check if we just reached max speed
                const isNowAtMax = newSpeed >= maxSpeed;

                // Only trigger max effects if:
                // 1. We weren't at max before
                // 2. We are at max now
                // 3. We haven't triggered it yet this session (or it was reset)
                if (wasNotAtMax && isNowAtMax) {
                    if (this.sfxManager && !this.hasTriggeredMaxThisSession) {
                        // Set flag IMMEDIATELY to prevent race conditions from rapid clicks
                        this.hasTriggeredMaxThisSession = true;

                        // Play revMax sound and trigger effects
                        this.sfxManager.playRevMax();
                        this.triggerMaxSpeedEffects();

                        // Show MAX! popup from drive button
                        this.showMaxSpeedPopup();
                    }
                }
            }
        };

        // Mouse click handler
        // Hold-to-activate autopilot tracking
        let holdTimer = null;
        let holdStartTime = null;
        let clickX = 0;
        let clickY = 0;

        const startHold = () => {
            if (holdTimer) return; // Already holding

            holdStartTime = performance.now();
            holdTimer = setTimeout(() => {
                // After 1 second, activate cruise control
                const currentTime = performance.now() / 1000;
                const result = this.driveStateMachine.activateCruiseControl(currentTime);

                if (result.activated) {
                    this.showMaxSpeedPopup();
                }

                // Animate momentum display falling if momentum was lost
                if (result.lostMomentum) {
                    this.playMomentumLossAnimation();
                }

                holdTimer = null;
            }, 1000);
        };

        const cancelHold = () => {
            if (holdTimer) {
                clearTimeout(holdTimer);
                holdTimer = null;

                // If released before 1 second, trigger normal drive
                const holdDuration = performance.now() - holdStartTime;
                if (holdDuration < 1000) {
                    // Use actual click coordinates
                    handleDrive(clickX, clickY);
                }
            }
        };

        // Mouse events for drive button
        driveButton.addEventListener('mousedown', (e) => {
            // Capture actual click coordinates
            clickX = e.clientX;
            clickY = e.clientY;
            startHold();
        });

        driveButton.addEventListener('mouseup', (e) => {
            cancelHold();
        });

        driveButton.addEventListener('mouseleave', (e) => {
            if (holdTimer) {
                clearTimeout(holdTimer);
                holdTimer = null;
            }
        });

        // Spacebar handler with hold detection
        let spaceHeld = false;

        document.addEventListener('keydown', (e) => {
            // Don't capture if typing in an input/textarea or modal is open
            if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
            if (document.querySelector('.cf-modal-overlay')) return;

            if (e.code === 'Space' && this.elements.driveButton) {
                e.preventDefault(); // Prevent page scroll

                // Prevent repeated events from holding key down
                if (e.repeat) {
                    return;
                }

                // Set click position to center of drive button for popup spawning
                const rect = this.elements.driveButton.getBoundingClientRect();
                clickX = rect.left + rect.width / 2;
                clickY = rect.top + rect.height / 2;

                spaceHeld = true;
                startHold();
            }
        });

        document.addEventListener('keyup', (e) => {
            if (e.code === 'Space' && spaceHeld) {
                e.preventDefault();
                spaceHeld = false;
                cancelHold();
            }
        });

        driveButtonContainer.appendChild(driveButton);
        this.elements.driveButton = driveButton;

        // Progress bar inside drive button - styled via game-hud.css
        const progressBar = document.createElement('div');
        progressBar.id = 'drive-progress-bar';
        progressBar.className = 'cf-progress-bar';
        driveButton.appendChild(progressBar);
        this.elements.progressBar = progressBar;

        // Cruise control button (below drive button) - styled via game-hud.css
        const cruiseControlButton = document.createElement('button');
        cruiseControlButton.id = 'cruise-control-button';

        // Progress bar (behind text)
        const cruiseProgressBar = document.createElement('div');
        cruiseProgressBar.id = 'cruise-progress-bar';
        cruiseControlButton.appendChild(cruiseProgressBar);
        this.elements.cruiseProgressBar = cruiseProgressBar;

        // Text span (above progress bar)
        const cruiseButtonText = document.createElement('span');
        cruiseButtonText.id = 'cruise-button-text';
        cruiseButtonText.textContent = 'AUTOPILOT';
        cruiseControlButton.appendChild(cruiseButtonText);
        this.elements.cruiseButtonText = cruiseButtonText;

        cruiseControlButton.addEventListener('click', () => {
            // Block clicks while autopilot is active
            if (this.driveStateMachine.isCruiseControlActive()) {
                return;
            }

            const currentTime = performance.now() / 1000;
            const result = this.driveStateMachine.activateCruiseControl(currentTime);

            // Show MAX! popup when cruise control is successfully activated
            if (result.activated) {
                this.showMaxSpeedPopup();
            }

            // Animate momentum display falling if momentum was lost
            if (result.lostMomentum) {
                this.playMomentumLossAnimation();
            }
        });
        driveButtonContainer.appendChild(cruiseControlButton);
        this.elements.cruiseControlButton = cruiseControlButton;

        // Spend Miles button (bottom right) - styled via game-hud.css
        const spendMilesButton = document.createElement('button');
        spendMilesButton.id = 'spend-miles-button';

        // Create text span for miles display
        const spendMilesText = document.createElement('span');
        spendMilesText.textContent = '0 MI';
        spendMilesButton.appendChild(spendMilesText);
        this.elements.spendMilesText = spendMilesText;

        spendMilesButton.addEventListener('click', () => {
            this.showUpgradeMenu();
        });
        uiContainer.appendChild(spendMilesButton);
        this.elements.spendMilesButton = spendMilesButton;

        // Skill available badge (on top left of Spend Miles button) - styled via game-hud.css
        const skillAvailBadge = document.createElement('img');
        skillAvailBadge.id = 'skill-avail-badge';
        skillAvailBadge.src = './assets/ui/skillAvail.png';
        spendMilesButton.appendChild(skillAvailBadge);
        this.elements.skillAvailBadge = skillAvailBadge;

        // Pack notification badge (on top right of CAR button)
        // Shows "NEW" when there are unseen waves or items
        const packNotificationBadge = document.createElement('div');
        packNotificationBadge.id = 'pack-notification-badge';
        packNotificationBadge.textContent = 'NEW';
        // Styles are in game-hud.css - only set display:none here for initial state
        spendMilesButton.appendChild(packNotificationBadge);
        this.elements.packNotificationBadge = packNotificationBadge;

        // Add animations that are still needed for dynamic effects
        const style = document.createElement('style');
        style.textContent = `
            /* Prevent zoom on mobile when tapping buttons */
            button {
                touch-action: manipulation;
            }
            /* Default (centered) momentum animations */
            @keyframes momentumPulse {
                0%, 100% {
                    opacity: 0.8;
                    transform: translateX(-50%) scale(1);
                }
                50% {
                    opacity: 1;
                    transform: translateX(-50%) scale(1.05);
                }
            }
            @keyframes momentumFallOut {
                0% {
                    opacity: 1;
                    transform: translateX(-50%) translateY(0);
                }
                100% {
                    opacity: 0;
                    transform: translateX(-50%) translateY(100px);
                }
            }
            @keyframes momentumFadeIn {
                0% {
                    opacity: 0;
                    transform: translateX(-50%) scale(0.9);
                }
                100% {
                    opacity: 1;
                    transform: translateX(-50%) scale(1);
                }
            }
            /* Landscape (bottom-left) momentum animations */
            @keyframes momentumPulseLandscape {
                0%, 100% {
                    opacity: 0.8;
                    transform: scale(1);
                }
                50% {
                    opacity: 1;
                    transform: scale(1.05);
                }
            }
            @keyframes momentumFallOutLandscape {
                0% {
                    opacity: 1;
                    transform: translateY(0);
                }
                100% {
                    opacity: 0;
                    transform: translateY(100px);
                }
            }
            @keyframes momentumFadeInLandscape {
                0% {
                    opacity: 0;
                    transform: scale(0.9);
                }
                100% {
                    opacity: 1;
                    transform: scale(1);
                }
            }
        `;
        document.head.appendChild(style);

        // Create flex container for top buttons
        const topButtonRow = document.createElement('div');
        topButtonRow.id = 'top-button-row';
        uiContainer.appendChild(topButtonRow);

        // Back button - returns to main menu and starts simulating
        const backButton = document.createElement('button');
        backButton.id = 'music-button'; // Reuse same ID for existing styles
        backButton.textContent = '←';
        backButton.title = 'Return to Menu';
        backButton.addEventListener('click', () => {
            // Directly unload game without confirmation modal
            if (this.app && typeof this.app.unloadGame === 'function') {
                this.app.unloadGame();
            }
        });
        topButtonRow.appendChild(backButton);
        this.elements.backButton = backButton;

        // Music toggle button - next to back button
        const gameMusicToggle = document.createElement('button');
        gameMusicToggle.id = 'game-music-toggle';
        // Set initial state based on whether music is already playing
        const isCurrentlyPlaying = this.app?.youtubePlayer?.isCurrentlyPlaying();
        if (isCurrentlyPlaying) gameMusicToggle.classList.add('playing');
        gameMusicToggle.title = isCurrentlyPlaying ? 'Pause Music' : 'Play Music';
        gameMusicToggle.addEventListener('click', () => {
            if (this.app?.youtubePlayer) {
                const isPlaying = this.app.youtubePlayer.toggle();
                gameMusicToggle.classList.toggle('playing', isPlaying);
                gameMusicToggle.title = isPlaying ? 'Pause Music' : 'Play Music';

                // When YouTube starts, stop menu loop permanently for this session
                if (isPlaying && this.app.menuLoopPlayer) {
                    this.app.menuLoopPlayer.stop(true); // true = mark YouTube as having played
                }

                // Sync with start screen button if it exists
                const startScreenBtn = document.getElementById('music-toggle-button');
                if (startScreenBtn) {
                    startScreenBtn.classList.toggle('playing', isPlaying);
                    startScreenBtn.title = isPlaying ? 'Pause Music' : 'Play Music';
                }
            }
        });
        topButtonRow.appendChild(gameMusicToggle);
        this.elements.gameMusicToggle = gameMusicToggle;

        // Firebase cloud buttons (always create, visibility controlled by auth state)
        if (this.app?.accountManager) {
            // Leaderboard button
            const leaderboardBtn = document.createElement('button');
            leaderboardBtn.id = 'cloud-leaderboard-btn';
            leaderboardBtn.className = 'cloud-button';
            leaderboardBtn.textContent = '🏆';
            leaderboardBtn.title = 'Leaderboards';
            leaderboardBtn.style.display = 'none'; // Hidden until auth confirmed
            leaderboardBtn.addEventListener('click', () => {
                if (this.app?.leaderboardManager) {
                    showLeaderboardModal({
                        leaderboardManager: this.app.leaderboardManager,
                        modalManager: this.modals
                    });
                }
            });
            topButtonRow.appendChild(leaderboardBtn);
            this.elements.leaderboardBtn = leaderboardBtn;

            // Friends button with notification badge
            const friendsBtn = document.createElement('button');
            friendsBtn.id = 'cloud-friends-btn';
            friendsBtn.className = 'cloud-button';
            friendsBtn.style.position = 'relative';
            friendsBtn.style.display = 'none'; // Hidden until auth confirmed
            friendsBtn.textContent = '👥';
            friendsBtn.title = 'Friends';

            // Notification badge for pending friend requests
            const badge = document.createElement('span');
            badge.id = 'friends-notification-badge';
            badge.style.cssText = `
                position: absolute;
                top: -4px;
                right: -4px;
                background: #e74c3c;
                color: white;
                font-size: 10px;
                font-weight: bold;
                min-width: 16px;
                height: 16px;
                border-radius: 8px;
                display: none;
                align-items: center;
                justify-content: center;
                padding: 0 4px;
                box-sizing: border-box;
            `;
            friendsBtn.appendChild(badge);

            friendsBtn.addEventListener('click', () => {
                if (this.app?.friendManager) {
                    showInGameAccountModal({
                        friendManager: this.app.friendManager,
                        accountManager: this.app.accountManager,
                        cloudSaveManager: this.app.cloudSaveManager,
                        modalManager: this.modals
                    });
                    // Refresh badge when modal closes
                    setTimeout(() => this.updateFriendRequestBadge(), 500);
                }
            });
            topButtonRow.appendChild(friendsBtn);
            this.elements.friendsBtn = friendsBtn;
            this.elements.friendsBadge = badge;

            // Set up auth state listener to show/hide cloud buttons
            this.app.accountManager.onAuthStateChanged((user) => {
                this.updateCloudButtonsVisibility();
            });

            // Initial visibility check (with small delay for auth to initialize)
            setTimeout(() => this.updateCloudButtonsVisibility(), 100);
            setTimeout(() => this.updateCloudButtonsVisibility(), 500);
            setTimeout(() => this.updateCloudButtonsVisibility(), 1500);

            // Periodic badge refresh every 30 seconds using UIUpdateScheduler (Phase 6 TMR-02)
            this._friendBadgeUpdateId = 'friend-badge-update';
            const scheduler = getUIUpdateScheduler();
            scheduler.register(this._friendBadgeUpdateId, () => {
                this.updateFriendRequestBadge();
            }, { interval: 30000 });
        }

        // Bug report button - always available
        const bugBtn = document.createElement('button');
        bugBtn.id = 'cloud-bug-btn';
        bugBtn.className = 'cloud-button';
        bugBtn.textContent = '🐞';
        bugBtn.title = 'Report Bug';
        bugBtn.addEventListener('click', () => {
            showBugReportModal({
                gameState: this.gameState,
                accountManager: this.app?.accountManager
            });
        });
        topButtonRow.appendChild(bugBtn);
        this.elements.bugBtn = bugBtn;
    }

    /**
     * Update cloud buttons visibility based on auth state
     * Called on auth state changes and periodically at startup
     */
    updateCloudButtonsVisibility() {
        const accountManager = this.app?.accountManager;
        const isLoggedIn = accountManager?.currentUser &&
            !accountManager.currentUser.isAnonymous;

        const leaderboardBtn = this.elements.leaderboardBtn;
        const friendsBtn = this.elements.friendsBtn;

        if (leaderboardBtn) {
            leaderboardBtn.style.display = isLoggedIn ? '' : 'none';
        }
        if (friendsBtn) {
            friendsBtn.style.display = isLoggedIn ? '' : 'none';
        }

        // Update badge if now visible
        if (isLoggedIn) {
            this.updateFriendRequestBadge();
        }

        console.log('[GameUI] Cloud buttons visibility updated, isLoggedIn:', isLoggedIn);
    }

    /**
     * Update the friend request notification badge
     */
    async updateFriendRequestBadge() {
        const badge = this.elements.friendsBadge;
        if (!badge || !this.app?.friendManager) {
            return;
        }

        // Ensure user is logged in (not anonymous)
        const accountManager = this.app?.accountManager;
        if (!accountManager?.currentUser || accountManager.currentUser.isAnonymous) {
            badge.style.display = 'none';
            return;
        }

        try {
            const pendingRequests = await this.app.friendManager.getPendingRequests();
            const count = pendingRequests.length;

            if (count > 0) {
                badge.textContent = count > 9 ? '9+' : count.toString();
                badge.style.display = 'flex';
            } else {
                badge.style.display = 'none';
            }
        } catch (e) {
            // Silently fail - badge just won't update
            badge.style.display = 'none';
        }
    }

    createControlButton(label, onClick) {
        const button = document.createElement('button');
        button.textContent = label;
        button.style.cssText = `
            font-family: 'Courier New', monospace;
            font-size: 18px;
            padding: 10px 14px;
            background: rgba(50, 160, 100, 0.75);
            color: rgba(255, 255, 255, 0.9);
            border: 1px solid rgba(255, 255, 255, 0.2);
            border-radius: 6px;
            cursor: pointer;
            transition: background-color 0.25s ease, border-color 0.25s ease, transform 0.25s ease;
        `;
        button.addEventListener('mouseover', () => {
            button.style.background = 'rgba(60, 180, 110, 0.85)';
            button.style.borderColor = 'rgba(255, 255, 255, 0.35)';
            button.style.transform = 'translateY(-2px)';
        });
        button.addEventListener('mouseout', () => {
            button.style.background = 'rgba(50, 160, 100, 0.75)';
            button.style.borderColor = 'rgba(255, 255, 255, 0.2)';
            button.style.transform = 'translateY(0)';
        });
        button.addEventListener('click', onClick);
        return button;
    }

    openMusicModal() {
        if (this.musicModalOpen) return;
        this.musicModalOpen = true;

        // Create modal overlay
        const overlay = document.createElement('div');
        overlay.style.cssText = `
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background: rgba(5, 5, 10, 0.92);
            z-index: 10000;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            padding: 20px;
            backdrop-filter: blur(8px);
        `;

        // Modal content
        const modal = document.createElement('div');
        modal.style.cssText = `
            background: rgba(15, 15, 25, 0.95);
            border: 1px solid rgba(255, 255, 255, 0.15);
            border-radius: 12px;
            padding: 32px;
            max-width: 380px;
            width: 100%;
            text-align: center;
            box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
        `;

        // Title
        const title = document.createElement('div');
        title.textContent = 'MUSIC';
        title.style.cssText = `
            font-family: 'Courier New', monospace;
            font-size: 16px;
            font-weight: normal;
            letter-spacing: 3px;
            color: rgba(255, 255, 255, 0.8);
            margin-bottom: 24px;
            text-transform: uppercase;
        `;
        modal.appendChild(title);

        // Playback controls container
        const controlsContainer = document.createElement('div');
        controlsContainer.style.cssText = `
            display: flex;
            gap: 10px;
            justify-content: center;
            margin-bottom: 20px;
        `;

        // Play/Stop button - show correct initial state
        const youtubePlayer = this.app?.youtubePlayer;
        const isMusicPlaying = youtubePlayer?.isCurrentlyPlaying();
        const playStopBtn = this.createControlButton(isMusicPlaying ? '⏹' : '▶', () => {
            if (youtubePlayer) {
                if (youtubePlayer.isCurrentlyPlaying()) {
                    youtubePlayer.pause();
                    playStopBtn.textContent = '▶';
                } else {
                    const volume = this.gameState.getMusicVolume();
                    youtubePlayer.setVolume(volume);
                    youtubePlayer.play();
                    playStopBtn.textContent = '⏹';

                    // When YouTube starts, stop menu loop permanently for this session
                    if (this.app.menuLoopPlayer) {
                        this.app.menuLoopPlayer.stop(true);
                    }
                }
            }
        });
        playStopBtn.style.fontSize = '24px';
        playStopBtn.style.padding = '12px 16px';

        controlsContainer.appendChild(playStopBtn);
        modal.appendChild(controlsContainer);

        // Mute settings container
        const muteContainer = document.createElement('div');
        muteContainer.style.cssText = `
            display: flex;
            flex-direction: column;
            gap: 12px;
            margin-bottom: 20px;
            padding: 16px;
            background: rgba(0, 0, 0, 0.2);
            border-radius: 8px;
        `;

        // SFX mute checkbox
        const sfxRow = document.createElement('label');
        sfxRow.style.cssText = `
            display: flex;
            align-items: center;
            gap: 12px;
            cursor: pointer;
            font-family: 'Courier New', monospace;
            font-size: 12px;
            color: rgba(255, 255, 255, 0.8);
        `;

        const sfxCheckbox = document.createElement('input');
        sfxCheckbox.type = 'checkbox';
        sfxCheckbox.checked = this.sfxManager ? !this.sfxManager.isMuted() : true;
        sfxCheckbox.style.cssText = `
            width: 18px;
            height: 18px;
            cursor: pointer;
            accent-color: rgba(100, 180, 100, 0.9);
        `;
        sfxCheckbox.addEventListener('change', () => {
            const muted = !sfxCheckbox.checked;
            if (this.sfxManager) {
                this.sfxManager.setMuted(muted);
            }
        });

        const sfxLabel = document.createElement('span');
        sfxLabel.textContent = 'SFX';
        sfxRow.appendChild(sfxCheckbox);
        sfxRow.appendChild(sfxLabel);
        muteContainer.appendChild(sfxRow);

        // Music mute checkbox
        const musicRow = document.createElement('label');
        musicRow.style.cssText = `
            display: flex;
            align-items: center;
            gap: 12px;
            cursor: pointer;
            font-family: 'Courier New', monospace;
            font-size: 12px;
            color: rgba(255, 255, 255, 0.8);
        `;

        const musicCheckbox = document.createElement('input');
        musicCheckbox.type = 'checkbox';
        musicCheckbox.checked = !this.gameState.isMusicMuted();
        musicCheckbox.style.cssText = `
            width: 18px;
            height: 18px;
            cursor: pointer;
            accent-color: rgba(100, 180, 100, 0.9);
        `;
        musicCheckbox.addEventListener('change', () => {
            const muted = !musicCheckbox.checked;
            this.gameState.setMusicMuted(muted);
            const youtubePlayer = this.app?.youtubePlayer;
            if (youtubePlayer) {
                if (muted) {
                    youtubePlayer.pause();
                    playStopBtn.textContent = '▶';
                } else {
                    const volume = this.gameState.getMusicVolume();
                    youtubePlayer.setVolume(volume > 0 ? volume : 50);
                    youtubePlayer.play();
                    playStopBtn.textContent = '⏹';

                    // When YouTube starts, stop menu loop permanently for this session
                    if (this.app.menuLoopPlayer) {
                        this.app.menuLoopPlayer.stop(true);
                    }
                }
            }
        });

        const musicLabel = document.createElement('span');
        musicLabel.textContent = 'MUSIC';
        musicRow.appendChild(musicCheckbox);
        musicRow.appendChild(musicLabel);
        muteContainer.appendChild(musicRow);

        modal.appendChild(muteContainer);

        // Close button
        const closeBtn = document.createElement('button');
        closeBtn.textContent = 'CLOSE';
        closeBtn.style.cssText = `
            width: 100%;
            font-family: 'Courier New', monospace;
            font-size: 11px;
            font-weight: normal;
            letter-spacing: 1px;
            padding: 14px 20px;
            background: rgba(100, 60, 60, 0.75);
            color: rgba(255, 255, 255, 0.85);
            border: 1px solid rgba(255, 255, 255, 0.15);
            border-radius: 6px;
            cursor: pointer;
            box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3);
            transition: all 0.25s ease;
            touch-action: manipulation;
        `;
        closeBtn.addEventListener('click', () => {
            document.body.removeChild(overlay);
            this.musicModalOpen = false;
        });
        modal.appendChild(closeBtn);

        overlay.appendChild(modal);
        document.body.appendChild(overlay);

        // Close on overlay click
        overlay.addEventListener('click', (e) => {
            if (e.target === overlay) {
                document.body.removeChild(overlay);
                this.musicModalOpen = false;
            }
        });
    }

    update(currentTime = performance.now() / 1000) {
        // Update game clock display (bottom left)
        if (this.elements.gameClockDisplay && this.gameState) {
            const gameTimeStr = this.gameState.getGameTimeString();
            this.elements.gameClockDisplay.textContent = gameTimeStr;

            // Update time compression badge
            if (this.elements.timeCompressionBadge && this.app.statCalculator) {
                const effects = this.app.statCalculator.calculateActiveItemEffects();
                const timeScaleEffect = effects.timeScale;

                // Check if there's any time compression bonus from items
                if (timeScaleEffect && timeScaleEffect.multiply > 1) {
                    const multiplier = timeScaleEffect.multiply.toFixed(1);
                    this.elements.timeCompressionBadge.textContent = `${multiplier}x`;
                    this.elements.timeCompressionBadge.style.display = 'block';
                } else {
                    this.elements.timeCompressionBadge.style.display = 'none';
                }
            }
        }

        // Update miles display (top center - spendable miles) - only if changed
        if (this.elements.milesDisplay) {
            const miles = Math.floor(this.gameState.getMiles());
            if (miles !== this.lastMiles) {
                // Check if we gained miles (not lost from purchases)
                if (this.lastMiles !== undefined && miles > this.lastMiles) {
                    const milesGained = miles - this.lastMiles;

                    // Show +X popup for miles
                    this.showNewMilePopup(milesGained);

                    if (this.sfxManager) {
                        this.sfxManager.playNewMile();
                    }
                }
                this.elements.milesDisplay.textContent = `${miles.toLocaleString()} MI`;
                this.lastMiles = miles;

                // Update skill available badge
                this.updateSkillAvailBadge();

                // Update CAR button text
                if (this.elements.spendMilesText) {
                    const milesText = `${miles.toLocaleString()} MI`;
                    this.elements.spendMilesText.textContent = milesText;
                }

                // Update pack notification badge
                this.updatePackNotificationBadge();
            }
        }

        // Update drive button
        if (this.elements.driveButton) {
            const inBoostMode = this.driveStateMachine.isInBoostMode();

            // Reset MAX! trigger when exiting boost mode
            if (this.hasTriggeredMaxThisSession && !inBoostMode) {
                this.hasTriggeredMaxThisSession = false;
            }
            const currentMph = Math.round(this.driveStateMachine.getSpeedMPH());
            const newButtonText = `${currentMph} MPH`;

            if (this.elements.driveButton.textContent !== newButtonText) {
                this.elements.driveButton.textContent = newButtonText;
            }

            // Toggle boost-mode class based on state
            if (inBoostMode) {
                if (!this.elements.driveButton.classList.contains('boost-mode')) {
                    this.elements.driveButton.classList.add('boost-mode');
                }
            } else {
                if (this.elements.driveButton.classList.contains('boost-mode')) {
                    this.elements.driveButton.classList.remove('boost-mode');
                }
            }

            // Drive button color staging based on speed progress
            const currentSpeed = this.driveStateMachine.getSpeedMPH();
            const maxSpeed = this.gameState.getCurrentMaxSpeed();
            const speedProgress = Math.min(currentSpeed / maxSpeed, 1);
            const atMaxSpeed = speedProgress >= 0.99;
            const canBoost = atMaxSpeed && !inBoostMode;

            if (canBoost) {
                // At max speed and can boost - soft red background
                this.elements.driveButton.style.background = 'rgba(140, 60, 60, 0.6)';
            } else if (inBoostMode) {
                // During boost - brighter red
                this.elements.driveButton.style.background = 'rgba(160, 70, 70, 0.7)';
            } else if (speedProgress > 0.01) {
                // Lerp green background from dim to full brightness based on speed
                // At 0% speed: rgba(50, 120, 90, 0.15)
                // At 100% speed: rgba(50, 120, 90, 0.7)
                const colorOpacity = 0.15 + (speedProgress * 0.55);
                this.elements.driveButton.style.background = `rgba(50, 120, 90, ${colorOpacity.toFixed(2)})`;
            } else {
                // No speed - default transparent black
                this.elements.driveButton.style.background = 'rgba(0, 0, 0, 0.4)';
            }
        }

        // Update drive button state during autopilot
        if (this.elements.driveButton) {
            const cruiseActive = this.driveStateMachine.isCruiseControlActive();
            if (cruiseActive && !this.elements.driveButton.classList.contains('autopilot-active')) {
                this.elements.driveButton.classList.add('autopilot-active');
            } else if (!cruiseActive && this.elements.driveButton.classList.contains('autopilot-active')) {
                this.elements.driveButton.classList.remove('autopilot-active');
            }
        }

        // Update cruise control button (cached to prevent redundant updates)
        if (this.elements.cruiseControlButton) {
            const cruiseActive = this.driveStateMachine.isCruiseControlActive();
            const cooldownRemaining = this.driveStateMachine.getCruiseControlCooldownRemaining(currentTime);
            const cruiseDuration = this.driveStateMachine.getCurrentCruiseDuration();
            const cruiseCooldown = this.driveStateMachine.getCurrentCruiseCooldown();

            let newButtonText = '';
            let newState = ''; // 'active', 'cooldown', or ''
            let progressPercent = 100;

            if (cruiseActive) {
                const timeRemaining = this.driveStateMachine.getCruiseControlTimeRemaining(currentTime);
                const seconds = Math.ceil(timeRemaining);
                newButtonText = `AUTOPILOT: ${seconds}S`;
                newState = 'active';
                // Green bar drains as time runs out
                progressPercent = (timeRemaining / cruiseDuration) * 100;
            } else if (cooldownRemaining > 0) {
                const seconds = Math.ceil(cooldownRemaining);
                newButtonText = `COOLDOWN: ${seconds}S`;
                newState = 'cooldown';
                // Blue bar fills as cooldown progresses
                progressPercent = ((cruiseCooldown - cooldownRemaining) / cruiseCooldown) * 100;
            } else {
                newButtonText = `AUTOPILOT (${Math.round(cruiseDuration)}S)`;
                newState = '';
                // Ready state - full green bar
                progressPercent = 100;
            }

            // Update progress bar width (always update for smooth animation)
            if (this.elements.cruiseProgressBar) {
                this.elements.cruiseProgressBar.style.width = `${progressPercent}%`;
            }

            // Only update text if changed
            if (newButtonText !== this.lastCruiseButtonText) {
                this.elements.cruiseButtonText.textContent = newButtonText;
                this.elements.cruiseControlButton.classList.remove('active', 'cooldown');
                if (newState) {
                    this.elements.cruiseControlButton.classList.add(newState);
                }
                this.lastCruiseButtonText = newButtonText;
            }
        }

        // Update momentum display with idle timer and progress bar
        if (this.elements.momentumDisplay) {
            const momentum = this.driveStateMachine.getMomentumBonus();
            const momentumCap = this.driveStateMachine.getCurrentMomentumCap();

            // Check if in mobile landscape mode (use landscape animations without translateX)
            const isLandscapeMobile = window.innerHeight < 500 && window.innerWidth > window.innerHeight;

            if (momentum > 0.1) {
                // Update text
                this.elements.momentumText.textContent = `Momentum Bonus: +${momentum.toFixed(1)} MPH`;

                // Update progress bar width (lerps naturally as momentum increases)
                const progressPercent = Math.min(100, (momentum / momentumCap) * 100);
                this.elements.momentumProgressBar.style.width = `${progressPercent}%`;

                // Check if this is the first time showing (fade in)
                if (!this.momentumDisplayWasVisible) {
                    this.elements.momentumDisplay.style.display = 'block';
                    const fadeInAnim = isLandscapeMobile ? 'momentumFadeInLandscape' : 'momentumFadeIn';
                    this.elements.momentumDisplay.style.animation = `${fadeInAnim} 0.4s ease-out forwards`;

                    // After fade-in, switch to pulsing
                    setTimeout(() => {
                        if (this.elements.momentumDisplay) {
                            const pulseAnim = isLandscapeMobile ? 'momentumPulseLandscape' : 'momentumPulse';
                            this.elements.momentumDisplay.style.animation = `${pulseAnim} 2s ease-in-out infinite`;
                        }
                    }, 400);

                    this.momentumDisplayWasVisible = true;
                } else {
                    this.elements.momentumDisplay.style.display = 'block';
                }
            } else if (!this.momentumLossAnimationActive) {
                // Only hide if animation is not playing
                this.elements.momentumDisplay.style.display = 'none';
                this.momentumDisplayWasVisible = false;
                // Reset progress bar
                this.elements.momentumProgressBar.style.width = '0%';
            }
        }

        // Update FOV based on speed percentage
        this.updateSpeedFOV();
    }

    showUpgradeMenu(restoreScrollPos = 0) {
        this.upgradeMenu.show(restoreScrollPos);
    }

    // Delegate to StatsFormatter
    getStatDisplayName(statType) {
        return StatsFormatter.getStatDisplayName(statType);
    }

    getNextTierForCap(level) {
        return StatsFormatter.getNextTierForCap(level);
    }

    getStatBreakdown(type, level, carConfig) {
        return StatsFormatter.getStatBreakdown(type, level, carConfig, this.gameState);
    }

    getCurrentStatValue(type, level, carConfig) {
        return StatsFormatter.getCurrentStatValue(type, level, carConfig, this.gameState);
    }

    hasStatBonuses(type, carConfig) {
        return StatsFormatter.hasStatBonuses(type, this.gameState);
    }

    getUpgradeStatsText(type, level, carConfig, atMaxLevel) {
        return StatsFormatter.getUpgradeStatsText(type, level, carConfig, this.gameState);
    }

    getWaveTier(packQuality) {
        return StatsFormatter.getWaveTier(packQuality);
    }

    createItemDisplay(item, container, isEquipped, parentOverlay) {
        const itemName = document.createElement('div');
        // For legacy items: show level. For v2 items: no level display
        if (item.isLegacy && item.level !== undefined) {
            const levelDisplay = item.level >= 10 ? ' (MAX)' : ` (Lv ${item.level})`;
            itemName.textContent = item.name + levelDisplay;
        } else {
            itemName.textContent = item.name;
        }
        itemName.style.cssText = `
            font-size: 12px;
            font-weight: normal;
            letter-spacing: 0.3px;
            margin-bottom: 4px;
            color: ${this.app.itemsData.rarityColors[item.rarity]};
        `;
        container.appendChild(itemName);

        const itemType = document.createElement('div');
        // Show LEGACY badge for legacy items, just slot/rarity for v3 bespoke items
        // Use slotType if available, fall back to slot for compatibility
        const slotDisplay = (item.slotType || item.slot || 'item').toUpperCase();
        if (item.isLegacy) {
            itemType.appendChild(document.createTextNode(`${slotDisplay} | ${item.rarity.toUpperCase()} | `));
            const legacyBadge = document.createElement('span');
            legacyBadge.style.cssText = 'color: #f59e0b; background: rgba(245, 158, 11, 0.15); padding: 1px 4px; border-radius: 3px; font-weight: bold;';
            legacyBadge.textContent = 'LEGACY';
            itemType.appendChild(legacyBadge);
        } else {
            // v3 bespoke items - no star ratings (fixed tier values instead)
            itemType.appendChild(document.createTextNode(`${slotDisplay} | ${item.rarity.toUpperCase()}`));
        }
        itemType.style.cssText = `
            font-size: 9px;
            color: rgba(255, 255, 255, 0.5);
            margin-bottom: 6px;
        `;
        container.appendChild(itemType);

        // Effects
        const effectsList = document.createElement('div');
        effectsList.style.cssText = `
            font-size: 10px;
            color: rgba(255, 255, 255, 0.55);
            line-height: 1.3;
        `;

        item.effects.forEach(effect => {
            const effectLine = document.createElement('div');
            const statName = this.getStatDisplayName(effect.type);
            const unit = StatsFormatter.getStatUnit(effect.type);
            let effectText = '';

            // Determine if this effect should be displayed as a multiplier (percentage)
            // Use the effect's operation if set, otherwise infer from stat type
            const isMultiplier = effect.operation === 'multiply' ||
                (effect.operation !== 'add' && (effect.type === 'acceleration' || effect.type === 'timeScale'));
            const isPercentageStat = StatsFormatter.isPercentageStat(effect.type);
            const isMultiplierStat = StatsFormatter.isMultiplierStat(effect.type);
            const isDurationStat = StatsFormatter.isDurationStat(effect.type);

            if (isMultiplier) {
                // Multiplicative effects - show as +X%
                const percent = ((effect.value - 1) * 100).toFixed(0);
                effectText = `${percent >= 0 ? '+' : ''}${percent}% ${statName}`;
            } else if (isMultiplierStat) {
                // Multiplier stats - show as 1.XXx
                const multiplier = 1 + effect.value;
                effectText = `${multiplier.toFixed(2)}x ${statName}`;
            } else if (isPercentageStat) {
                // Percentage stats stored as decimals (0.04 = 4%)
                const percent = Math.round(effect.value * 100);
                effectText = `${percent >= 0 ? '+' : ''}${percent}% ${statName}`;
            } else if (isDurationStat) {
                // Duration stats - show as Xm Ys format
                const sign = effect.value >= 0 ? '+' : '';
                effectText = `${sign}${StatsFormatter.formatDurationValue(Math.abs(effect.value))} ${statName}`;
            } else {
                // Additive effects - show as +X.X value with units
                // Momentum rate needs 3 decimal places (small values like 0.005)
                const decimals = effect.type === 'momentumRate' ? 3 : 1;
                const formattedValue = Number(effect.value.toFixed(decimals));
                const unitSuffix = unit ? ' ' + unit : '';
                effectText = `${formattedValue >= 0 ? '+' : ''}${formattedValue}${unitSuffix} ${statName}`;
            }

            if (effect.condition && effect.condition.timeOfDay) {
                effectText += ` (${effect.condition.timeOfDay})`;
            }

            effectLine.textContent = effectText;
            effectLine.style.cssText = `
                color: rgba(120, 200, 150, 0.9);
                margin-bottom: 2px;
            `;
            effectsList.appendChild(effectLine);
        });

        container.appendChild(effectsList);
    }

    showPackCardSelection(items, packMilestone, packCost = 0, onPurchaseComplete = null, wavedustEarned = 0) {
        if (items.length === 0) {
            alert('No items available! All items have been collected.');
            return;
        }

        // Selection state
        let selectedItem = null;
        let selectedCard = null;

        // Slot colors and icons (matching collection modal)
        const slotColors = {
            'rearview': '#44aaff',
            'dashboard': '#a78bfa',
            'keychain': '#c8a030'
        };
        const slotIcons = { 'rearview': '🪞', 'dashboard': '📊', 'keychain': '🔑' };
        const slotDisplayNames = {
            dashboard: 'DASHBOARD',
            rearview: 'REARVIEW',
            keychain: 'KEYCHAIN'
        };
        const rarityColors = this.app.itemsData.rarityColors;

        // Detect layout mode
        const isPhone = this.isPhoneScreen();
        const isPortrait = window.innerHeight > window.innerWidth;
        const isCompact = isPhone && isPortrait;

        // Create CF-styled modal overlay (matching UpgradeMenuManager pattern)
        const overlay = document.createElement('div');
        overlay.className = 'cf-modal-overlay';
        overlay.style.cssText = `
            position: fixed;
            inset: 0;
            background: rgba(11, 13, 18, 0.92);
            backdrop-filter: blur(4px);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 10000;
            padding: 16px;
        `;

        // Modal container
        const modal = document.createElement('div');
        modal.className = 'cf-modal cf-item-choice-modal';
        modal.style.cssText = `
            background: var(--cf-bg-primary, #11131A);
            border: 1px solid var(--cf-divider, #2A2F3C);
            max-width: 520px;
            width: 100%;
            max-height: 90vh;
            display: flex;
            flex-direction: column;
            overflow: hidden;
        `;

        // Close function
        const close = () => {
            overlay.remove();
        };

        // Click outside to close
        overlay.addEventListener('click', (e) => {
            if (e.target === overlay) close();
        });

        // Escape to close
        const handleEscape = (e) => {
            if (e.key === 'Escape') {
                close();
                document.removeEventListener('keydown', handleEscape);
            }
        };
        document.addEventListener('keydown', handleEscape);

        // Header
        const header = document.createElement('div');
        header.style.cssText = `
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 14px 20px;
            border-bottom: 1px solid var(--cf-divider, #2A2F3C);
        `;

        const title = document.createElement('div');
        title.textContent = 'CHOOSE ONE ITEM';
        title.style.cssText = `
            font-family: var(--cf-font-mono);
            font-size: 10px;
            font-weight: 500;
            color: var(--cf-text-tertiary, #5C6170);
            letter-spacing: 0.12em;
            text-transform: uppercase;
        `;
        header.appendChild(title);

        const closeBtn = document.createElement('button');
        closeBtn.textContent = '×';
        closeBtn.style.cssText = `
            background: none;
            border: none;
            color: var(--cf-text-tertiary, #5C6170);
            font-size: 20px;
            cursor: pointer;
            padding: 0;
            line-height: 1;
        `;
        closeBtn.addEventListener('click', close);
        header.appendChild(closeBtn);
        modal.appendChild(header);

        // Body
        const body = document.createElement('div');
        body.style.cssText = `
            padding: 16px;
            overflow-y: auto;
            flex: 1;
            display: flex;
            flex-direction: column;
            gap: 14px;
        `;

        // Show wavedust earned popup
        if (wavedustEarned > 0) {
            const wavedustPopup = document.createElement('div');
            wavedustPopup.textContent = `+${wavedustEarned} ⚙`;
            wavedustPopup.style.cssText = `
                position: absolute;
                top: 50%;
                left: 50%;
                transform: translate(-50%, -50%);
                font-family: var(--cf-font-mono);
                font-size: 28px;
                font-weight: 500;
                color: var(--cf-lavender, #a78bfa);
                text-shadow: 0 0 12px rgba(167, 139, 250, 0.6);
                pointer-events: none;
                z-index: 100;
                animation: wavedustPopupRise 2s ease-out forwards;
            `;
            if (!document.getElementById('wavedust-popup-keyframes')) {
                const style = document.createElement('style');
                style.id = 'wavedust-popup-keyframes';
                style.textContent = `
                    @keyframes wavedustPopupRise {
                        0% { opacity: 1; transform: translate(-50%, -50%) scale(1); }
                        20% { opacity: 1; transform: translate(-50%, -50%) scale(1.2); }
                        100% { opacity: 0; transform: translate(-50%, -150%) scale(1.1); }
                    }
                `;
                document.head.appendChild(style);
            }
            overlay.appendChild(wavedustPopup);
            setTimeout(() => wavedustPopup.remove(), 2000);
        }

        // Sizing
        const cardWidth = isCompact ? '100px' : '140px';
        const spriteSize = isCompact ? '32px' : '36px';

        const equippedItems = this.gameState.getEquippedItems();

        // Helper to check if item is new to collection
        const isNewToCollection = (item) => {
            if (!item.baseId) return false;
            return !this.gameState.collectionStateManager?.hasFoundItem(item.baseId);
        };

        // Helper to format effect text
        const formatEffect = (effect) => {
            const statName = this.getStatDisplayName(effect.type);
            const unit = StatsFormatter.getStatUnit(effect.type);
            const isMultiplier = effect.operation === 'multiply' ||
                (effect.operation !== 'add' && (effect.type === 'acceleration' || effect.type === 'timeScale'));
            const isPercentageStat = StatsFormatter.isPercentageStat(effect.type);
            const isMultiplierStat = StatsFormatter.isMultiplierStat(effect.type);
            const isDurationStat = StatsFormatter.isDurationStat(effect.type);

            if (isMultiplier) {
                const percent = ((effect.value - 1) * 100).toFixed(0);
                return `${percent >= 0 ? '+' : ''}${percent}% ${statName}`;
            } else if (isMultiplierStat) {
                const multiplier = 1 + effect.value;
                return `${multiplier.toFixed(2)}x ${statName}`;
            } else if (isPercentageStat) {
                const percent = Math.round(effect.value * 100);
                return `${percent >= 0 ? '+' : ''}${percent}% ${statName}`;
            } else if (isDurationStat) {
                const sign = effect.value >= 0 ? '+' : '';
                return `${sign}${StatsFormatter.formatDurationValue(Math.abs(effect.value))} ${statName}`;
            } else {
                const decimals = effect.type === 'momentumRate' ? 3 : 1;
                const formattedValue = Number(effect.value.toFixed(decimals));
                const unitSuffix = unit ? ' ' + unit : '';
                return `${formattedValue >= 0 ? '+' : ''}${formattedValue}${unitSuffix} ${statName}`;
            }
        };

        // === ITEM CARDS ROW ===
        const cardsRow = document.createElement('div');
        cardsRow.style.cssText = `
            display: flex;
            gap: 10px;
            justify-content: center;
            flex-wrap: wrap;
        `;

        const cardElements = [];

        // Build item card
        const buildItemCard = (item, index) => {
            const card = document.createElement('div');
            card.dataset.index = index;
            const itemSlot = item.slotType || item.slot;
            card.dataset.slotType = itemSlot;
            const slotColor = slotColors[itemSlot] || '#888';
            const rarityColor = rarityColors[item.rarity] || '#888';
            const isNew = isNewToCollection(item);

            card.style.cssText = `
                background: var(--cf-bg-deep, #0B0D12);
                border: 1px solid var(--cf-divider, #2A2F3C);
                padding: 10px;
                width: ${cardWidth};
                cursor: pointer;
                transition: all 0.15s ease;
                font-family: var(--cf-font-mono);
                position: relative;
                display: flex;
                flex-direction: column;
                align-items: center;
                text-align: center;
            `;

            // NEW badge
            if (isNew) {
                const newBadge = document.createElement('div');
                newBadge.textContent = 'NEW';
                newBadge.style.cssText = `
                    position: absolute;
                    top: -5px;
                    right: -5px;
                    background: rgba(200, 150, 80, 0.95);
                    color: #000;
                    font-size: 7px;
                    font-weight: bold;
                    padding: 2px 4px;
                    border-radius: 2px;
                    letter-spacing: 0.5px;
                `;
                card.appendChild(newBadge);
            }

            // Sprite with slot-colored background
            const spriteContainer = document.createElement('div');
            spriteContainer.style.cssText = `
                width: ${spriteSize};
                height: ${spriteSize};
                background: ${slotColor}20;
                border: 1px solid ${slotColor}50;
                border-radius: 4px;
                display: flex;
                align-items: center;
                justify-content: center;
                font-size: ${isCompact ? '14px' : '18px'};
                margin-bottom: 6px;
            `;
            spriteContainer.textContent = slotIcons[itemSlot] || '📦';
            card.appendChild(spriteContainer);

            // Item name
            const nameEl = document.createElement('div');
            nameEl.textContent = item.name;
            nameEl.style.cssText = `
                font-size: 9px;
                font-weight: 600;
                color: var(--cf-text-primary, #F5F5F5);
                text-transform: uppercase;
                letter-spacing: 0.3px;
                margin-bottom: 2px;
                width: 100%;
                text-align: center;
                line-height: 1.3;
            `;
            card.appendChild(nameEl);

            // Tier label (rarity color, not slot color)
            const tierEl = document.createElement('div');
            tierEl.textContent = item.rarity.toUpperCase();
            tierEl.style.cssText = `
                font-size: 8px;
                color: ${rarityColor};
                letter-spacing: 0.5px;
                margin-bottom: 6px;
                padding-bottom: 4px;
                border-bottom: 1px solid var(--cf-divider, #2A2F3C);
                width: 100%;
            `;
            card.appendChild(tierEl);

            // Stats as bonus boxes
            const statsContainer = document.createElement('div');
            const effectsHtml = item.effects.slice(0, 2).map(eff =>
                `<span class="cf-bonus-box cf-bonus-box--cyan cf-bonus-box--xs">${formatEffect(eff)}</span>`
            ).join('');
            statsContainer.innerHTML = `<div class="cf-bonus-row cf-bonus-row--centered cf-bonus-row--compact cf-bonus-row--no-margin">${effectsHtml}</div>`;
            card.appendChild(statsContainer);

            return card;
        };

        items.forEach((item, index) => {
            const card = buildItemCard(item, index);
            const itemSlot = item.slotType || item.slot;
            const slotColor = slotColors[itemSlot] || '#888';

            card.addEventListener('click', () => {
                if (selectedCard && selectedCard !== card) {
                    selectedCard.style.borderColor = 'var(--cf-divider, #2A2F3C)';
                }
                selectedItem = item;
                selectedCard = card;
                card.style.borderColor = slotColor;
                updateReplacesSection();
                enableButtons();
            });

            card.addEventListener('mouseenter', () => {
                if (card !== selectedCard) card.style.borderColor = slotColor + '66';
            });
            card.addEventListener('mouseleave', () => {
                if (card !== selectedCard) card.style.borderColor = 'var(--cf-divider, #2A2F3C)';
            });

            cardElements.push(card);
            cardsRow.appendChild(card);
        });

        body.appendChild(cardsRow);

        // === REPLACES SECTION ===
        const replacesSection = document.createElement('div');

        const replacesHeader = document.createElement('div');
        replacesHeader.textContent = 'SELECT AN ITEM ABOVE';
        replacesHeader.style.cssText = `
            font-family: var(--cf-font-mono);
            font-size: 9px;
            color: var(--cf-text-tertiary, #5C6170);
            letter-spacing: 1px;
            margin-bottom: 6px;
            text-align: center;
        `;
        replacesSection.appendChild(replacesHeader);

        const replacesContent = document.createElement('div');
        replacesContent.style.cssText = `
            background: var(--cf-bg-deep, #0B0D12);
            border: 1px solid var(--cf-divider, #2A2F3C);
            padding: 10px;
            display: flex;
            align-items: center;
            gap: 10px;
            min-height: 56px;
        `;
        const placeholderText = document.createElement('div');
        placeholderText.textContent = 'Tap an item to see what it replaces';
        placeholderText.style.cssText = `
            width: 100%;
            text-align: center;
            color: var(--cf-text-tertiary, #5C6170);
            font-family: var(--cf-font-mono);
            font-size: 10px;
        `;
        replacesContent.appendChild(placeholderText);
        replacesSection.appendChild(replacesContent);

        const updateReplacesSection = () => {
            if (!selectedItem) return;
            const itemSlot = selectedItem.slotType || selectedItem.slot;
            const equippedItem = equippedItems[itemSlot];
            const slotColor = slotColors[itemSlot] || '#888';
            const eqRarityColor = equippedItem ? (rarityColors[equippedItem.rarity] || '#888') : '#888';

            replacesContent.innerHTML = '';

            if (equippedItem) {
                replacesHeader.textContent = `REPLACES IN ${slotDisplayNames[itemSlot]}`;
                replacesHeader.style.color = slotColor;

                // Square sprite
                const eqSprite = document.createElement('div');
                eqSprite.style.cssText = `
                    width: 40px;
                    height: 40px;
                    background: ${slotColor}20;
                    border: 1px solid ${slotColor}50;
                    border-radius: 4px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 20px;
                    flex-shrink: 0;
                `;
                eqSprite.textContent = slotIcons[itemSlot] || '📦';
                replacesContent.appendChild(eqSprite);

                const eqDetails = document.createElement('div');
                eqDetails.style.cssText = `display: flex; flex-direction: column; gap: 2px; flex: 1; min-width: 0;`;

                const eqName = document.createElement('div');
                eqName.textContent = equippedItem.name;
                eqName.style.cssText = `font-family: var(--cf-font-mono); font-size: 10px; font-weight: 600; color: var(--cf-text-primary); text-transform: uppercase;`;
                eqDetails.appendChild(eqName);

                const eqTier = document.createElement('div');
                eqTier.textContent = equippedItem.rarity.toUpperCase();
                eqTier.style.cssText = `font-family: var(--cf-font-mono); font-size: 8px; color: ${eqRarityColor}; letter-spacing: 0.5px;`;
                eqDetails.appendChild(eqTier);

                const eqStats = document.createElement('div');
                const effectsHtml = equippedItem.effects.slice(0, 2).map(eff =>
                    `<span class="cf-bonus-box cf-bonus-box--cyan cf-bonus-box--xs">${formatEffect(eff)}</span>`
                ).join('');
                eqStats.innerHTML = `<div class="cf-bonus-row cf-bonus-row--left cf-bonus-row--compact cf-bonus-row--no-margin">${effectsHtml}</div>`;
                eqDetails.appendChild(eqStats);

                replacesContent.appendChild(eqDetails);
            } else {
                replacesHeader.textContent = `EQUIPS TO ${slotDisplayNames[itemSlot]}`;
                replacesHeader.style.color = slotColor;

                const emptyText = document.createElement('div');
                emptyText.textContent = '(nothing equipped)';
                emptyText.style.cssText = `width: 100%; text-align: center; color: var(--cf-text-tertiary); font-family: var(--cf-font-mono); font-size: 10px;`;
                replacesContent.appendChild(emptyText);
            }
        };

        body.appendChild(replacesSection);

        // === BUTTONS ===
        const buttonsContainer = document.createElement('div');
        buttonsContainer.style.cssText = `
            display: flex;
            flex-direction: column;
            gap: 8px;
            align-items: center;
            margin-top: 4px;
        `;

        const mainButtonsRow = document.createElement('div');
        mainButtonsRow.style.cssText = `display: flex; gap: 10px; justify-content: center;`;

        // TAKE button
        const takeBtn = document.createElement('button');
        takeBtn.textContent = 'TAKE';
        takeBtn.style.cssText = `
            font-family: var(--cf-font-mono);
            font-size: 10px;
            font-weight: 500;
            padding: 8px 20px;
            background: transparent;
            color: var(--cf-text-tertiary, #5C6170);
            border: 1px solid var(--cf-divider, #2A2F3C);
            cursor: not-allowed;
            transition: all 0.15s ease;
            opacity: 0.5;
            pointer-events: none;
            letter-spacing: 0.08em;
        `;
        takeBtn.addEventListener('click', () => {
            if (!selectedItem) return;
            this.gameState.confirmPackPurchase(packMilestone);
            this.gameState.addItem(selectedItem);
            this.sfxManager?.playSound('upgrade');
            this.showItemObtainedNotification(selectedItem);
            this.updatePackNotificationBadge();
            close();
            if (onPurchaseComplete) onPurchaseComplete();
        });
        mainButtonsRow.appendChild(takeBtn);

        // TAKE & EQUIP button
        const takeEquipBtn = document.createElement('button');
        takeEquipBtn.textContent = 'TAKE & EQUIP';
        takeEquipBtn.style.cssText = `
            font-family: var(--cf-font-mono);
            font-size: 10px;
            font-weight: 500;
            padding: 8px 20px;
            background: transparent;
            color: var(--cf-text-tertiary, #5C6170);
            border: 1px solid var(--cf-divider, #2A2F3C);
            cursor: not-allowed;
            transition: all 0.15s ease;
            opacity: 0.5;
            pointer-events: none;
            letter-spacing: 0.08em;
        `;
        takeEquipBtn.addEventListener('click', () => {
            if (!selectedItem) return;
            this.gameState.confirmPackPurchase(packMilestone);
            this.gameState.addItem(selectedItem);
            this.gameState.equipItem(selectedItem.id, selectedItem.slotType);
            this.sfxManager?.playSound('upgrade');
            this.showItemObtainedNotification(selectedItem);
            this.updatePackNotificationBadge();
            close();
            if (onPurchaseComplete) onPurchaseComplete();
        });
        mainButtonsRow.appendChild(takeEquipBtn);
        buttonsContainer.appendChild(mainButtonsRow);

        // SCRAP BEST button
        let bestItem = null;
        let bestScrapValue = 0;
        items.forEach(item => {
            const value = this.gameState.getItemScrapValueDirect(item);
            if (value > bestScrapValue) {
                bestScrapValue = value;
                bestItem = item;
            }
        });

        if (bestItem && bestScrapValue > 0) {
            const scrapBestBtn = document.createElement('button');
            scrapBestBtn.textContent = `Ⓧ SCRAP BEST +${bestScrapValue} ⚙`;
            scrapBestBtn.style.cssText = `
                font-family: var(--cf-font-mono);
                font-size: 9px;
                padding: 5px 12px;
                background: transparent;
                color: var(--cf-negative, #E86A6A);
                border: 1px solid var(--cf-negative, #E86A6A);
                cursor: pointer;
                transition: all 0.15s ease;
                letter-spacing: 0.05em;
            `;

            scrapBestBtn.addEventListener('mouseenter', () => {
                scrapBestBtn.style.background = 'rgba(232, 106, 106, 0.15)';
            });
            scrapBestBtn.addEventListener('mouseleave', () => {
                scrapBestBtn.style.background = 'transparent';
            });

            scrapBestBtn.addEventListener('click', () => {
                // Show confirmation dialog
                this._showScrapConfirmation(bestItem, bestScrapValue, packMilestone, close, onPurchaseComplete);
            });

            buttonsContainer.appendChild(scrapBestBtn);
        }

        body.appendChild(buttonsContainer);

        // Helper to enable buttons
        const enableButtons = () => {
            takeBtn.style.opacity = '1';
            takeBtn.style.cursor = 'pointer';
            takeBtn.style.pointerEvents = 'auto';
            takeBtn.style.color = 'var(--cf-text-primary, #F5F5F5)';
            takeBtn.style.borderColor = 'var(--cf-divider, #2A2F3C)';

            takeEquipBtn.style.opacity = '1';
            takeEquipBtn.style.cursor = 'pointer';
            takeEquipBtn.style.pointerEvents = 'auto';
            takeEquipBtn.style.color = 'var(--cf-cyan, #5FB3B3)';
            takeEquipBtn.style.borderColor = 'var(--cf-cyan, #5FB3B3)';
        };

        // Assemble modal
        modal.appendChild(body);
        overlay.appendChild(modal);
        document.body.appendChild(overlay);
    }

    /**
     * Show scrap confirmation dialog
     */
    _showScrapConfirmation(item, scrapValue, packMilestone, closeParent, onPurchaseComplete) {
        const slotColors = {
            'rearview': '#44aaff',
            'dashboard': '#a78bfa',
            'keychain': '#c8a030'
        };
        const slotIcons = { 'rearview': '🪞', 'dashboard': '📊', 'keychain': '🔑' };
        const itemSlot = item.slotType || item.slot;
        const slotColor = slotColors[itemSlot] || '#888';
        const rarityColor = this.app.itemsData.rarityColors[item.rarity] || '#888';

        // Build message content as HTMLElement
        const messageContent = document.createElement('div');
        messageContent.style.cssText = `
            display: flex;
            flex-direction: column;
            gap: 12px;
        `;

        // Item preview card
        const itemPreview = document.createElement('div');
        itemPreview.style.cssText = `
            display: flex;
            align-items: center;
            gap: 12px;
            background: var(--cf-bg-deep, #0B0D12);
            border: 1px solid var(--cf-divider, #2A2F3C);
            padding: 12px;
        `;

        const sprite = document.createElement('div');
        sprite.style.cssText = `
            width: 40px;
            height: 40px;
            background: ${slotColor}20;
            border: 1px solid ${slotColor}50;
            border-radius: 4px;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 20px;
            flex-shrink: 0;
        `;
        sprite.textContent = slotIcons[itemSlot] || '📦';
        itemPreview.appendChild(sprite);

        const details = document.createElement('div');
        details.style.cssText = `display: flex; flex-direction: column; gap: 2px; flex: 1;`;

        const nameEl = document.createElement('div');
        nameEl.textContent = item.name;
        nameEl.style.cssText = `
            font-family: var(--cf-font-mono);
            font-size: 11px;
            font-weight: 600;
            color: var(--cf-text-primary, #F5F5F5);
            text-transform: uppercase;
        `;
        details.appendChild(nameEl);

        const tierEl = document.createElement('div');
        tierEl.textContent = item.rarity.toUpperCase();
        tierEl.style.cssText = `
            font-family: var(--cf-font-mono);
            font-size: 8px;
            color: ${rarityColor};
            letter-spacing: 0.5px;
        `;
        details.appendChild(tierEl);

        const statsEl = document.createElement('div');
        const formatEffect = (effect) => {
            const statName = this.getStatDisplayName(effect.type);
            const unit = StatsFormatter.getStatUnit(effect.type);
            const isMultiplier = effect.operation === 'multiply' ||
                (effect.operation !== 'add' && (effect.type === 'acceleration' || effect.type === 'timeScale'));
            const isPercentageStat = StatsFormatter.isPercentageStat(effect.type);
            const isMultiplierStat = StatsFormatter.isMultiplierStat(effect.type);
            const isDurationStat = StatsFormatter.isDurationStat(effect.type);
            if (isMultiplier) {
                const percent = ((effect.value - 1) * 100).toFixed(0);
                return `${percent >= 0 ? '+' : ''}${percent}% ${statName}`;
            } else if (isMultiplierStat) {
                const multiplier = 1 + effect.value;
                return `${multiplier.toFixed(2)}x ${statName}`;
            } else if (isPercentageStat) {
                const percent = Math.round(effect.value * 100);
                return `${percent >= 0 ? '+' : ''}${percent}% ${statName}`;
            } else if (isDurationStat) {
                const sign = effect.value >= 0 ? '+' : '';
                return `${sign}${StatsFormatter.formatDurationValue(Math.abs(effect.value))} ${statName}`;
            } else {
                const decimals = effect.type === 'momentumRate' ? 3 : 1;
                const formattedValue = Number(effect.value.toFixed(decimals));
                const unitSuffix = unit ? ' ' + unit : '';
                return `${formattedValue >= 0 ? '+' : ''}${formattedValue}${unitSuffix} ${statName}`;
            }
        };
        const effectsHtml = item.effects.slice(0, 2).map(eff =>
            `<span class="cf-bonus-box cf-bonus-box--cyan cf-bonus-box--compact">${formatEffect(eff)}</span>`
        ).join('');
        statsEl.innerHTML = `<div class="cf-bonus-row cf-bonus-row--centered cf-bonus-row--compact cf-bonus-row--no-margin">${effectsHtml}</div>`;
        details.appendChild(statsEl);

        itemPreview.appendChild(details);
        messageContent.appendChild(itemPreview);

        // Warning text
        const warning = document.createElement('div');
        warning.style.cssText = `
            font-family: var(--cf-font-mono);
            font-size: 11px;
            color: var(--cf-text-secondary, rgba(255,255,255,0.7));
            text-align: center;
            line-height: 1.6;
        `;
        warning.innerHTML = `This item will be permanently scrapped for <span style="color: var(--cf-lavender, #a78bfa); font-weight: 600;">+${scrapValue} ⚙</span> Salvage.`;
        messageContent.appendChild(warning);

        // Use createConfirmation with the message content
        this.modals.createConfirmation({
            title: 'SCRAP BEST ITEM?',
            message: messageContent,
            cancelText: 'CANCEL',
            confirmText: `SCRAP +${scrapValue} ⚙`,
            confirmStyle: 'danger',
            onConfirm: () => {
                const multiplier = this.gameState.getScrapBonusMultiplier(true);
                const totalValue = Math.floor(scrapValue * multiplier);

                this.gameState.addWavedust(totalValue);
                this.gameState.confirmPackPurchase(packMilestone);
                this.sfxManager?.playSound('upgrade');

                // Show popup near center of screen
                const bonusPercent = (this.gameState.getScrapBonusMultiplier() - 1) * 100;
                this.popups.showScrapWavedust(
                    window.innerWidth / 2,
                    window.innerHeight / 2 - 50,
                    totalValue,
                    bonusPercent
                );

                this.updatePackNotificationBadge();
                closeParent();

                if (onPurchaseComplete) onPurchaseComplete();
            }
        });
    }

    showItemObtainedNotification(item) {
        const rarityColor = this.app.itemsData.rarityColors[item.rarity];
        this.popups.showItemObtained(item, rarityColor, (item, container, isEquipped, parentOverlay) => {
            this.createItemDisplay(item, container, isEquipped, parentOverlay);
        });
    }

    showAscensionScreen(newCarId, parentOverlay) {
        // Calculate ascension preview
        const ascension = this.gameState.calculateAscension(newCarId);
        const newCarConfig = this.gameState.carsConfig[newCarId];
        const oldCarConfig = this.gameState.carsConfig[ascension.oldCar.id];
        const oldTierColor = this.gameState.getTierColor(ascension.oldCar.id);

        // Create modal overlay
        const modalOverlay = document.createElement('div');
        modalOverlay.style.cssText = `
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background: rgba(0, 0, 0, 0.95);
            z-index: 350;
            display: flex;
            align-items: center;
            justify-content: center;
            pointer-events: auto;
            backdrop-filter: blur(4px);
        `;

        // Create modal content
        const modal = document.createElement('div');
        modal.style.cssText = `
            background: rgba(12, 12, 20, 0.98);
            border: 2px solid rgba(255, 180, 100, 0.6);
            border-radius: 12px;
            padding: 30px;
            max-width: 420px;
            width: 90vw;
            max-height: 85vh;
            overflow-y: auto;
            font-family: 'Courier New', monospace;
            color: rgba(255, 255, 255, 0.9);
            position: relative;
        `;

        // Title
        const title = document.createElement('div');
        title.textContent = 'ASCEND';
        title.style.cssText = `
            font-size: 11px;
            letter-spacing: 3px;
            color: rgba(255, 180, 100, 0.8);
            text-align: center;
            margin-bottom: 20px;
        `;
        modal.appendChild(title);

        // Current → New car display (compact)
        const carTransition = document.createElement('div');
        carTransition.style.cssText = `
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 15px;
            margin-bottom: 20px;
        `;

        // Old car
        const oldCarDisplay = document.createElement('div');
        oldCarDisplay.style.cssText = `text-align: center;`;

        const oldTierBadge = document.createElement('div');
        oldTierBadge.textContent = ascension.oldCar.tier.toUpperCase();
        oldTierBadge.style.cssText = `
            font-size: 8px;
            padding: 2px 5px;
            background: ${oldTierColor}22;
            color: ${oldTierColor};
            border: 1px solid ${oldTierColor}66;
            border-radius: 3px;
            letter-spacing: 0.5px;
            display: inline-block;
            margin-bottom: 4px;
        `;
        oldCarDisplay.appendChild(oldTierBadge);

        const oldCarName = document.createElement('div');
        oldCarName.textContent = oldCarConfig.name;
        oldCarName.style.cssText = `
            font-size: 12px;
            color: rgba(255, 255, 255, 0.7);
        `;
        oldCarDisplay.appendChild(oldCarName);
        carTransition.appendChild(oldCarDisplay);

        // Arrow
        const arrow = document.createElement('div');
        arrow.textContent = '→';
        arrow.style.cssText = `
            font-size: 20px;
            color: rgba(255, 180, 100, 0.8);
        `;
        carTransition.appendChild(arrow);

        // New car
        const newCarDisplay = document.createElement('div');
        newCarDisplay.style.cssText = `text-align: center;`;

        const newTierBadge = document.createElement('div');
        newTierBadge.textContent = ascension.newCar.tier.toUpperCase();
        newTierBadge.style.cssText = `
            font-size: 8px;
            padding: 2px 5px;
            background: ${ascension.newCar.tierColor}22;
            color: ${ascension.newCar.tierColor};
            border: 1px solid ${ascension.newCar.tierColor}66;
            border-radius: 3px;
            letter-spacing: 0.5px;
            display: inline-block;
            margin-bottom: 4px;
        `;
        newCarDisplay.appendChild(newTierBadge);

        const newCarName = document.createElement('div');
        newCarName.textContent = newCarConfig.name;
        newCarName.style.cssText = `
            font-size: 12px;
            color: rgba(255, 255, 255, 0.95);
        `;
        newCarDisplay.appendChild(newCarName);
        carTransition.appendChild(newCarDisplay);

        modal.appendChild(carTransition);

        // Stat name/unit mappings (used in stats section and details section)
        const upgradeNames = {
            baseSpeed: 'Idle Speed',
            acceleration: 'Acceleration',
            maxSpeed: 'Max Speed',
            cruiseDuration: 'Autopilot Dur',
            cruiseCooldown: 'Autopilot CD',
            momentum: 'Momentum'
        };
        const upgradeUnits = {
            baseSpeed: 'MPH',
            acceleration: 'MPH/click',
            maxSpeed: 'MPH',
            cruiseDuration: 's',
            cruiseCooldown: 's',
            momentum: 'MPH'
        };

        // Ascension Bonus preview (show bonuses that will be gained)
        const tierMult = ascension.newCar.statMultiplier;
        const ab = ascension.ascensionBonus;
        const prestigeBonuses = this.gameState.state.prestige?.skillBonuses || {};

        const statsSection = document.createElement('div');
        statsSection.style.cssText = `
            background: rgba(100, 160, 200, 0.08);
            border: 1px solid rgba(100, 160, 200, 0.2);
            border-radius: 6px;
            padding: 12px;
            margin-bottom: 15px;
        `;

        const statsLabel = document.createElement('div');
        statsLabel.textContent = `${newCarConfig.name} Stats`;
        statsLabel.style.cssText = `
            font-size: 9px;
            letter-spacing: 0.5px;
            color: rgba(100, 160, 200, 0.8);
            margin-bottom: 10px;
        `;
        statsSection.appendChild(statsLabel);

        // Stats to display with their configurations
        const statsToShow = [
            { key: 'baseSpeed', label: 'Idle Speed', unit: 'MPH', base: newCarConfig.baseSpeed },
            { key: 'acceleration', label: 'Acceleration', unit: 'MPH/click', base: newCarConfig.baseAcceleration },
            { key: 'maxSpeed', label: 'Max Speed', unit: 'MPH', base: newCarConfig.maxSpeed },
            { key: 'cruiseDuration', label: 'Autopilot Dur', unit: 's', base: newCarConfig.baseCruiseDuration },
            { key: 'cruiseCooldown', label: 'Autopilot CD', unit: 's', base: newCarConfig.baseCruiseCooldown, isReduction: true },
            { key: 'momentum', label: 'Momentum', unit: 'MPH', base: newCarConfig.baseMomentumCap },
            { key: 'aerodynamics', label: 'Aerodynamics', unit: '', base: newCarConfig.aerodynamics, isPassive: true }
        ];

        statsToShow.forEach(stat => {
            const baseValue = stat.base;

            // Passive stats (like aerodynamics) have no bonuses
            const abValue = stat.isPassive ? 0 : (ab[stat.key] || 0);
            const prestigeBonus = stat.isPassive ? 0 : (prestigeBonuses[stat.key] || 0);

            // Calculate final value: (base + AB) * (1 + prestige), or for cooldown: (base + AB) / (1 + prestige)
            // Passive stats just use base value directly
            let finalValue;
            if (stat.isPassive) {
                finalValue = baseValue;
            } else if (stat.isReduction) {
                finalValue = (baseValue + abValue) / (1 + prestigeBonus);
            } else {
                finalValue = (baseValue + abValue) * (1 + prestigeBonus);
            }
            // Aerodynamics uses 2 decimal places, other stats use 1
            const precision = stat.isPassive ? 100 : 10;
            finalValue = Math.round(finalValue * precision) / precision;

            const statRow = document.createElement('div');
            statRow.style.cssText = `
                display: flex;
                justify-content: space-between;
                align-items: center;
                margin-bottom: 6px;
                font-size: 11px;
            `;

            const labelEl = document.createElement('span');
            labelEl.textContent = stat.label;
            labelEl.style.cssText = `color: rgba(255, 255, 255, 0.6);`;
            statRow.appendChild(labelEl);

            const rightSide = document.createElement('div');
            rightSide.style.cssText = `display: flex; align-items: center; gap: 8px;`;

            const valueEl = document.createElement('span');
            valueEl.textContent = `${finalValue}${stat.unit}`;
            valueEl.style.cssText = `color: rgba(255, 255, 255, 0.9); font-weight: bold;`;
            rightSide.appendChild(valueEl);

            // Info icon with tooltip (only show if there are bonuses)
            const hasAnyBonus = abValue !== 0 || prestigeBonus > 0;
            if (hasAnyBonus) {
                const infoIcon = document.createElement('span');
                infoIcon.textContent = 'ⓘ';
                infoIcon.style.cssText = `
                    color: rgba(100, 160, 200, 0.6);
                    cursor: help;
                    font-size: 12px;
                `;

                // Create tooltip (appended to body for proper positioning)
                const tooltip = document.createElement('div');
                tooltip.style.cssText = `
                    position: fixed;
                    background: rgba(20, 20, 30, 0.98);
                    border: 1px solid rgba(100, 160, 200, 0.4);
                    border-radius: 6px;
                    padding: 10px 12px;
                    min-width: 180px;
                    z-index: 10000;
                    display: none;
                    font-size: 10px;
                    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.5);
                    pointer-events: none;
                `;

                // Tooltip content - build with DOM
                const createTooltipRow = (label, value, labelColor, valueColor) => {
                    const row = document.createElement('div');
                    row.style.cssText = 'display: flex; justify-content: space-between; margin-bottom: 4px;';
                    const labelSpan = document.createElement('span');
                    labelSpan.style.color = labelColor;
                    labelSpan.textContent = label;
                    row.appendChild(labelSpan);
                    const valueSpan = document.createElement('span');
                    valueSpan.style.color = valueColor;
                    valueSpan.textContent = value;
                    row.appendChild(valueSpan);
                    return row;
                };

                const titleDiv = document.createElement('div');
                titleDiv.style.cssText = 'color: rgba(255, 255, 255, 0.9); font-weight: bold; margin-bottom: 8px;';
                titleDiv.textContent = stat.label;
                tooltip.appendChild(titleDiv);

                tooltip.appendChild(createTooltipRow(
                    `Base (${newCarConfig.name})`,
                    `${baseValue}${stat.unit}`,
                    'rgba(255, 255, 255, 0.5)',
                    'rgba(255, 255, 255, 0.7)'
                ));

                if (abValue !== 0) {
                    const abSign = abValue > 0 ? '+' : '';
                    const displayAB = Math.abs(abValue) >= 10 ? Math.round(abValue) : abValue.toFixed(1);
                    tooltip.appendChild(createTooltipRow(
                        'Ascension Bonus',
                        `${abSign}${displayAB}${stat.unit}`,
                        'rgba(120, 200, 140, 0.8)',
                        'rgba(120, 200, 140, 0.9)'
                    ));
                }

                if (prestigeBonus > 0) {
                    const prestigePercent = Math.round(prestigeBonus * 100);
                    const prestigeLabel = stat.isReduction ? 'reduction' : 'bonus';
                    tooltip.appendChild(createTooltipRow(
                        'Prestige Bonus',
                        `+${prestigePercent}% ${prestigeLabel}`,
                        'rgba(200, 150, 255, 0.8)',
                        'rgba(200, 150, 255, 0.9)'
                    ));
                }

                const totalRow = document.createElement('div');
                totalRow.style.cssText = 'border-top: 1px solid rgba(255, 255, 255, 0.1); margin-top: 6px; padding-top: 6px; display: flex; justify-content: space-between;';
                const totalLabel = document.createElement('span');
                totalLabel.style.color = 'rgba(255, 255, 255, 0.7)';
                totalLabel.textContent = 'Total';
                totalRow.appendChild(totalLabel);
                const totalValue = document.createElement('span');
                totalValue.style.cssText = 'color: rgba(255, 255, 255, 0.95); font-weight: bold;';
                totalValue.textContent = `${finalValue}${stat.unit}`;
                totalRow.appendChild(totalValue);
                tooltip.appendChild(totalRow);
                document.body.appendChild(tooltip);

                // Show/hide tooltip on hover with safe positioning
                infoIcon.addEventListener('mouseenter', () => {
                    tooltip.style.display = 'block';
                    this.positionTooltip(tooltip, infoIcon);
                });
                infoIcon.addEventListener('mouseleave', () => {
                    tooltip.style.display = 'none';
                });

                // Touch support with safe positioning
                infoIcon.addEventListener('touchstart', (e) => {
                    e.preventDefault();
                    const isVisible = tooltip.style.display === 'block';
                    // Hide all other tooltips first
                    document.querySelectorAll('.ascension-tooltip').forEach(t => t.style.display = 'none');
                    if (!isVisible) {
                        tooltip.style.display = 'block';
                        this.positionTooltip(tooltip, infoIcon);
                    } else {
                        tooltip.style.display = 'none';
                    }
                });
                tooltip.classList.add('ascension-tooltip');

                rightSide.appendChild(infoIcon);
            }

            statRow.appendChild(rightSide);
            statsSection.appendChild(statRow);
        });

        modal.appendChild(statsSection);

        // Cost section (prestige-scaled)
        const currentMiles = this.gameState.getMiles();
        const purchaseCost = this.gameState.getCarUnlockCost(newCarId);
        const canAfford = currentMiles >= purchaseCost;

        const costSection = document.createElement('div');
        costSection.style.cssText = `
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 12px 0;
            border-top: 1px solid rgba(255, 255, 255, 0.1);
            margin-bottom: 20px;
        `;

        const costLabel = document.createElement('span');
        costLabel.textContent = 'Cost';
        costLabel.style.cssText = `
            font-size: 11px;
            color: rgba(255, 255, 255, 0.5);
        `;
        costSection.appendChild(costLabel);

        const costValue = document.createElement('span');
        costValue.textContent = `${purchaseCost.toLocaleString()} MI`;
        costValue.style.cssText = `
            font-size: 14px;
            color: ${canAfford ? 'rgba(255, 255, 255, 0.9)' : 'rgba(255, 100, 100, 0.8)'};
        `;
        costSection.appendChild(costValue);

        modal.appendChild(costSection);

        // Buttons
        const buttonContainer = document.createElement('div');
        buttonContainer.style.cssText = `
            display: flex;
            gap: 10px;
            justify-content: center;
            margin-bottom: 15px;
        `;

        const confirmButton = document.createElement('button');
        confirmButton.textContent = 'ASCEND';
        confirmButton.disabled = !canAfford;
        confirmButton.style.cssText = `
            font-family: 'Courier New', monospace;
            font-size: 12px;
            letter-spacing: 1px;
            padding: 12px 30px;
            background: ${canAfford ? 'rgba(180, 130, 60, 0.9)' : 'rgba(60, 60, 70, 0.5)'};
            color: ${canAfford ? 'rgba(255, 255, 255, 0.95)' : 'rgba(255, 255, 255, 0.4)'};
            border: 1px solid ${canAfford ? 'rgba(255, 180, 100, 0.6)' : 'rgba(255, 255, 255, 0.1)'};
            border-radius: 6px;
            cursor: ${canAfford ? 'pointer' : 'not-allowed'};
            transition: all 0.2s ease;
        `;
        if (canAfford) {
            confirmButton.addEventListener('mouseenter', () => {
                confirmButton.style.background = 'rgba(200, 150, 80, 0.95)';
            });
            confirmButton.addEventListener('mouseleave', () => {
                confirmButton.style.background = 'rgba(180, 130, 60, 0.9)';
            });
            confirmButton.addEventListener('click', () => {
                const result = this.gameState.purchaseCar(newCarId);
                if (result) {
                    modalOverlay.remove();
                    parentOverlay.remove();
                    // UpgradeMenuManager handles its own cleanup via UIUpdateScheduler
                    this.showUpgradeMenu(0);
                }
            });
        }
        buttonContainer.appendChild(confirmButton);

        const cancelButton = document.createElement('button');
        cancelButton.textContent = 'CANCEL';
        cancelButton.style.cssText = `
            font-family: 'Courier New', monospace;
            font-size: 12px;
            letter-spacing: 0.5px;
            padding: 12px 20px;
            background: rgba(60, 60, 70, 0.8);
            color: rgba(255, 255, 255, 0.7);
            border: 1px solid rgba(255, 255, 255, 0.2);
            border-radius: 6px;
            cursor: pointer;
            transition: all 0.2s ease;
        `;
        cancelButton.addEventListener('mouseenter', () => {
            cancelButton.style.background = 'rgba(80, 80, 90, 0.9)';
        });
        cancelButton.addEventListener('mouseleave', () => {
            cancelButton.style.background = 'rgba(60, 60, 70, 0.8)';
        });
        cancelButton.addEventListener('click', () => {
            modalOverlay.remove();
        });
        buttonContainer.appendChild(cancelButton);

        modal.appendChild(buttonContainer);

        // Details toggle (collapsed by default)
        const detailsContainer = document.createElement('div');
        detailsContainer.style.cssText = `text-align: center;`;

        const detailsToggle = document.createElement('button');
        detailsToggle.textContent = 'Show Details';
        detailsToggle.style.cssText = `
            font-family: 'Courier New', monospace;
            font-size: 10px;
            color: rgba(255, 255, 255, 0.4);
            background: none;
            border: none;
            cursor: pointer;
            padding: 5px 10px;
            transition: color 0.2s ease;
        `;
        detailsToggle.addEventListener('mouseenter', () => {
            detailsToggle.style.color = 'rgba(255, 255, 255, 0.7)';
        });
        detailsToggle.addEventListener('mouseleave', () => {
            if (detailsContent.style.display === 'none') {
                detailsToggle.style.color = 'rgba(255, 255, 255, 0.4)';
            }
        });

        const detailsContent = document.createElement('div');
        detailsContent.style.cssText = `
            display: none;
            margin-top: 15px;
            padding-top: 15px;
            border-top: 1px solid rgba(255, 255, 255, 0.1);
            text-align: left;
        `;

        // Build detailed breakdown
        const detailsGrid = document.createElement('div');
        detailsGrid.style.cssText = `
            display: grid;
            grid-template-columns: 1fr auto;
            gap: 6px 15px;
            font-size: 10px;
        `;

        const detailRows = [];

        // Get old car's upgrade levels for stat transfer display
        const oldCar = this.gameState.getCurrentCar();
        const transferRate = ascension.transfer.transferRatePercent;
        const tierName = ascension.oldCar.tier.charAt(0).toUpperCase() + ascension.oldCar.tier.slice(1);

        // SECTION 1: New Car Base Stats
        detailRows.push({ label: '── NEW CAR BASE STATS ──', value: '', color: 'rgba(255, 180, 100, 0.6)', header: true });
        detailRows.push({
            label: 'Base Speed',
            value: `${newCarConfig.baseSpeed} MPH`,
            color: 'rgba(255, 255, 255, 0.7)'
        });
        detailRows.push({
            label: 'Max Speed',
            value: `${newCarConfig.maxSpeed} MPH`,
            color: 'rgba(255, 255, 255, 0.7)'
        });
        detailRows.push({
            label: 'Acceleration',
            value: `${newCarConfig.baseAcceleration} MPH/click`,
            color: 'rgba(255, 255, 255, 0.7)'
        });

        // SECTION 2: Stat Transfer Bonus - Only show if there are stats being transferred
        const statTypes = ['baseSpeed', 'acceleration', 'maxSpeed', 'cruiseDuration', 'cruiseCooldown', 'momentum'];
        const hasAnyStatTransfer = statTypes.some(type => (oldCar.upgradeLevels?.[type] || 0) > 0);

        if (hasAnyStatTransfer) {
            detailRows.push({ label: '', value: '', color: 'transparent' }); // spacer
            detailRows.push({ label: `── STAT TRANSFER BONUS (${tierName} ${transferRate}%) ──`, value: '', color: 'rgba(255, 180, 100, 0.6)', header: true });

            // Show each stat's level transfer
            statTypes.forEach(type => {
                const currentLevel = oldCar.upgradeLevels?.[type] || 0;
                if (currentLevel > 0) {
                    const abValue = ascension.ascensionBonus[type] || 0;
                    const sign = type === 'cruiseCooldown' ? '-' : '+';
                    const displayValue = Math.abs(abValue) >= 10 ? Math.round(Math.abs(abValue)) : Math.abs(abValue).toFixed(1);
                    detailRows.push({
                        label: upgradeNames[type] || type,
                        value: `Lv${currentLevel} → ${sign}${displayValue}${upgradeUnits[type] || ''}`,
                        color: 'rgba(100, 180, 255, 0.9)'
                    });
                }
            });
        }

        detailRows.push({ label: '', value: '', color: 'transparent' }); // spacer

        // SECTION 3: Miles Transfer
        detailRows.push({ label: '── MILES TRANSFER ──', value: '', color: 'rgba(255, 180, 100, 0.6)', header: true });
        detailRows.push({
            label: 'Miles Invested',
            value: `${Math.floor(ascension.oldCar.milesInvested).toLocaleString()}`,
            color: 'rgba(255, 255, 255, 0.7)'
        });
        detailRows.push({
            label: 'Ascension Loss (25%)',
            value: `-${Math.floor(ascension.transfer.milesLost).toLocaleString()}`,
            color: 'rgba(255, 100, 100, 0.7)'
        });

        if (ascension.transfer.scalingApplied) {
            detailRows.push({
                label: `Car Scaling (${ascension.transfer.scalingPercent}%)`,
                value: `${Math.floor(ascension.transfer.milesAfterLoss).toLocaleString()} → ${Math.floor(ascension.transfer.milesTransferred).toLocaleString()}`,
                color: 'rgba(255, 200, 100, 0.7)'
            });
        }

        detailRows.push({
            label: 'Transfer Value',
            value: `${Math.floor(ascension.transfer.milesTransferred).toLocaleString()} MI`,
            color: 'rgba(120, 200, 140, 0.8)'
        });

        detailRows.push({ label: '', value: '', color: 'transparent' }); // spacer

        // SECTION 4: Ascension Bonuses (summary of what new car receives)
        const hasAnyBonus = Object.values(ascension.ascensionBonus).some(v => v !== 0);
        if (hasAnyBonus) {
            detailRows.push({ label: '── ASCENSION BONUSES ──', value: '', color: 'rgba(255, 180, 100, 0.6)', header: true });
            Object.entries(ascension.ascensionBonus).forEach(([type, value]) => {
                if (value !== 0) {
                    const sign = type === 'cruiseCooldown' ? '-' : '+';
                    const displayValue = Math.abs(value) >= 10 ? Math.round(Math.abs(value)) : Math.abs(value).toFixed(1);
                    detailRows.push({
                        label: upgradeNames[type] || type,
                        value: `AB ${sign}${displayValue}${upgradeUnits[type] || ''}`,
                        color: 'rgba(120, 200, 140, 0.9)'
                    });
                }
            });

            detailRows.push({ label: '', value: '', color: 'transparent' }); // spacer
        }

        // SECTION 5: Result
        detailRows.push({ label: '── RESULT ──', value: '', color: 'rgba(255, 180, 100, 0.6)', header: true });
        detailRows.push({
            label: 'New Tier',
            value: ascension.newCar.tier.charAt(0).toUpperCase() + ascension.newCar.tier.slice(1),
            color: ascension.newCar.tierColor
        });

        const tierMultiplier = ascension.newCar.statMultiplier;
        if (tierMultiplier > 1) {
            detailRows.push({
                label: 'Tier Stat Bonus',
                value: `+${Math.round((tierMultiplier - 1) * 100)}% to base stats`,
                color: 'rgba(120, 200, 140, 0.8)'
            });
        }

        detailRows.forEach(row => {
            if (row.label === '' && row.value === '') {
                // Spacer
                const spacer = document.createElement('div');
                spacer.style.cssText = `grid-column: span 2; height: 8px;`;
                detailsGrid.appendChild(spacer);
            } else if (row.header) {
                // Section header - spans both columns
                const headerEl = document.createElement('div');
                headerEl.textContent = row.label;
                headerEl.style.cssText = `
                    grid-column: span 2;
                    color: ${row.color};
                    font-size: 9px;
                    letter-spacing: 1px;
                    margin-top: 4px;
                    margin-bottom: 2px;
                `;
                detailsGrid.appendChild(headerEl);
            } else {
                const labelEl = document.createElement('span');
                labelEl.textContent = row.label;
                labelEl.style.cssText = `color: rgba(255, 255, 255, 0.5);${row.small ? ' font-size: 8px;' : ''}`;
                detailsGrid.appendChild(labelEl);

                const valueEl = document.createElement('span');
                valueEl.textContent = row.value;
                valueEl.style.cssText = `color: ${row.color}; text-align: right;${row.small ? ' font-size: 8px;' : ''}`;
                detailsGrid.appendChild(valueEl);
            }
        });

        detailsContent.appendChild(detailsGrid);

        detailsToggle.addEventListener('click', () => {
            if (detailsContent.style.display === 'none') {
                detailsContent.style.display = 'block';
                detailsToggle.textContent = 'Hide Details';
                detailsToggle.style.color = 'rgba(255, 255, 255, 0.6)';
            } else {
                detailsContent.style.display = 'none';
                detailsToggle.textContent = 'Show Details';
                detailsToggle.style.color = 'rgba(255, 255, 255, 0.4)';
            }
        });

        detailsContainer.appendChild(detailsToggle);
        detailsContainer.appendChild(detailsContent);
        modal.appendChild(detailsContainer);

        // Close on overlay click
        modalOverlay.addEventListener('click', (e) => {
            if (e.target === modalOverlay) {
                modalOverlay.remove();
            }
        });

        modalOverlay.appendChild(modal);
        document.body.appendChild(modalOverlay);
    }

    showPrestigeScreen(parentOverlay) {
        // Calculate prestige preview
        const prestigeLevel = this.gameState.state.prestige?.level || 0;
        const currentMiles = this.gameState.getMiles();
        const totalMiles = this.gameState.getTotalMiles();

        // Calculate bonuses using current totalMiles and available miles
        const bonusPreview = PrestigeSystem.calculatePrestigeBonus(totalMiles, currentMiles, prestigeLevel);

        // Current bonuses (already earned)
        const currentMileBonus = this.gameState.state.prestige?.mileEarningBonus || 0;
        const currentSkillBonuses = this.gameState.state.prestige?.skillBonuses || {};

        const tierName = PrestigeSystem.getPrestigeTierName(prestigeLevel);
        const tierColor = PrestigeSystem.getPrestigeTierColor(prestigeLevel);

        // Track selected skill
        let selectedSkill = 'baseSpeed'; // Default selection
        const nextTierName = PrestigeSystem.getPrestigeTierName(prestigeLevel + 1);
        const nextTierColor = PrestigeSystem.getPrestigeTierColor(prestigeLevel + 1);

        // Create modal overlay
        const modalOverlay = document.createElement('div');
        modalOverlay.style.cssText = `
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background: rgba(0, 0, 0, 0.95);
            z-index: 350;
            display: flex;
            align-items: center;
            justify-content: center;
            pointer-events: auto;
            backdrop-filter: blur(4px);
        `;

        // Create modal content
        const modal = document.createElement('div');
        modal.style.cssText = `
            background: rgba(12, 12, 20, 0.98);
            border: 2px solid ${prestigeLevel > 0 ? tierColor : 'rgba(200, 150, 255, 0.6)'};
            border-radius: 12px;
            padding: 30px;
            max-width: 420px;
            width: 90vw;
            max-height: 85vh;
            overflow-y: auto;
            font-family: 'Courier New', monospace;
            color: rgba(255, 255, 255, 0.9);
            position: relative;
        `;

        // Title
        const title = document.createElement('div');
        title.textContent = 'PRESTIGE';
        title.style.cssText = `
            font-size: 11px;
            letter-spacing: 3px;
            color: ${prestigeLevel > 0 ? tierColor : 'rgba(200, 150, 255, 0.8)'};
            text-align: center;
            margin-bottom: 5px;
        `;
        modal.appendChild(title);

        // Current level display
        if (prestigeLevel > 0) {
            const levelDisplay = document.createElement('div');
            levelDisplay.textContent = `${tierName} • Level ${prestigeLevel}`;
            levelDisplay.style.cssText = `
                font-size: 10px;
                color: ${tierColor};
                text-align: center;
                margin-bottom: 15px;
            `;
            modal.appendChild(levelDisplay);
        } else {
            const spacer = document.createElement('div');
            spacer.style.height = '10px';
            modal.appendChild(spacer);
        }

        // Description
        const description = document.createElement('div');
        description.textContent = 'Reset all progress to gain permanent bonuses that persist across runs.';
        description.style.cssText = `
            font-size: 10px;
            color: rgba(255, 255, 255, 0.5);
            text-align: center;
            margin-bottom: 20px;
            line-height: 1.4;
        `;
        modal.appendChild(description);

        // Current bonuses (if any)
        const hasCurrentBonuses = currentMileBonus > 0 || Object.values(currentSkillBonuses).some(v => v > 0);
        if (hasCurrentBonuses) {
            const currentSection = document.createElement('div');
            currentSection.style.cssText = `
                background: rgba(120, 200, 140, 0.08);
                border: 1px solid rgba(120, 200, 140, 0.2);
                border-radius: 6px;
                padding: 12px;
                margin-bottom: 15px;
            `;

            const currentLabel = document.createElement('div');
            currentLabel.textContent = 'CURRENT BONUSES';
            currentLabel.style.cssText = `
                font-size: 9px;
                letter-spacing: 0.5px;
                color: rgba(120, 200, 140, 0.8);
                margin-bottom: 8px;
            `;
            currentSection.appendChild(currentLabel);

            const currentGrid = document.createElement('div');
            currentGrid.style.cssText = `
                display: grid;
                grid-template-columns: 1fr auto;
                gap: 4px 12px;
                font-size: 10px;
            `;

            // Mile Earning (always first)
            if (currentMileBonus > 0) {
                const mileLabel = document.createElement('span');
                mileLabel.textContent = 'Mile Earning';
                mileLabel.style.cssText = `color: rgba(255, 255, 255, 0.6);`;
                currentGrid.appendChild(mileLabel);

                const mileValue = document.createElement('span');
                mileValue.textContent = PrestigeSystem.formatBonusMultiplier(currentMileBonus);
                mileValue.style.cssText = `color: rgba(120, 200, 140, 0.9); text-align: right;`;
                currentGrid.appendChild(mileValue);
            }

            // Show all skill bonuses that have value
            Object.entries(currentSkillBonuses).forEach(([skillId, bonus]) => {
                if (bonus > 0) {
                    const skill = PrestigeSystem.SELECTABLE_SKILLS[skillId];
                    const skillLabel = document.createElement('span');
                    skillLabel.textContent = skill ? skill.name : skillId;
                    skillLabel.style.cssText = `color: rgba(255, 255, 255, 0.6);`;
                    currentGrid.appendChild(skillLabel);

                    const skillValue = document.createElement('span');
                    skillValue.textContent = PrestigeSystem.formatBonusPercent(bonus);
                    skillValue.style.cssText = `color: rgba(120, 200, 140, 0.9); text-align: right;`;
                    currentGrid.appendChild(skillValue);
                }
            });

            currentSection.appendChild(currentGrid);
            modal.appendChild(currentSection);
        }

        // Base bonus from total miles
        const baseSection = document.createElement('div');
        baseSection.style.cssText = `
            background: rgba(80, 80, 120, 0.15);
            border: 1px solid rgba(255, 255, 255, 0.1);
            border-radius: 6px;
            padding: 12px;
            margin-bottom: 10px;
        `;

        const baseLabel = document.createElement('div');
        baseLabel.textContent = 'BASE BONUS (from Total Miles)';
        baseLabel.style.cssText = `
            font-size: 9px;
            letter-spacing: 0.5px;
            color: rgba(255, 255, 255, 0.5);
            margin-bottom: 8px;
        `;
        baseSection.appendChild(baseLabel);

        const baseGrid = document.createElement('div');
        baseGrid.style.cssText = `
            display: grid;
            grid-template-columns: 1fr auto;
            gap: 4px 12px;
            font-size: 10px;
        `;

        const baseMilesLabel = document.createElement('span');
        baseMilesLabel.textContent = `${Math.floor(totalMiles).toLocaleString()} total miles`;
        baseMilesLabel.style.cssText = `color: rgba(255, 255, 255, 0.6);`;
        baseGrid.appendChild(baseMilesLabel);

        const baseMilesValue = document.createElement('span');
        baseMilesValue.textContent = `→ ${PrestigeSystem.formatBonusPercent(bonusPreview.base)}`;
        baseMilesValue.style.cssText = `color: rgba(200, 150, 255, 0.9); text-align: right;`;
        baseGrid.appendChild(baseMilesValue);

        baseSection.appendChild(baseGrid);
        modal.appendChild(baseSection);

        // Extra bonus from available miles
        const extraSection = document.createElement('div');
        extraSection.style.cssText = `
            background: rgba(200, 180, 100, 0.1);
            border: 1px solid rgba(200, 180, 100, 0.3);
            border-radius: 6px;
            padding: 12px;
            margin-bottom: 10px;
        `;

        const extraLabel = document.createElement('div');
        extraLabel.textContent = 'EXTRA BONUS (from Unspent Miles)';
        extraLabel.style.cssText = `
            font-size: 9px;
            letter-spacing: 0.5px;
            color: rgba(200, 180, 100, 0.8);
            margin-bottom: 8px;
        `;
        extraSection.appendChild(extraLabel);

        const extraGrid = document.createElement('div');
        extraGrid.style.cssText = `
            display: grid;
            grid-template-columns: 1fr auto;
            gap: 4px 12px;
            font-size: 10px;
        `;

        const extraMilesLabel = document.createElement('span');
        extraMilesLabel.textContent = `${Math.floor(currentMiles).toLocaleString()} unspent miles`;
        extraMilesLabel.style.cssText = `color: rgba(200, 180, 100, 0.7);`;
        extraGrid.appendChild(extraMilesLabel);

        const extraMilesValue = document.createElement('span');
        extraMilesValue.textContent = `→ ${PrestigeSystem.formatBonusPercent(bonusPreview.extra)}`;
        extraMilesValue.style.cssText = `color: rgba(200, 180, 100, 0.9); text-align: right;`;
        extraGrid.appendChild(extraMilesValue);

        extraSection.appendChild(extraGrid);

        const extraNote = document.createElement('div');
        extraNote.textContent = 'Tip: Save miles before prestiging for extra bonus!';
        extraNote.style.cssText = `
            font-size: 9px;
            color: rgba(200, 180, 100, 0.6);
            margin-top: 6px;
            font-style: italic;
        `;
        extraSection.appendChild(extraNote);

        modal.appendChild(extraSection);

        // Level multiplier (if applicable)
        if (prestigeLevel > 0) {
            const multSection = document.createElement('div');
            multSection.style.cssText = `
                background: ${tierColor}15;
                border: 1px solid ${tierColor}44;
                border-radius: 6px;
                padding: 10px 12px;
                margin-bottom: 10px;
                display: flex;
                justify-content: space-between;
                align-items: center;
            `;

            const multLabel = document.createElement('span');
            multLabel.textContent = `Level ${prestigeLevel} Multiplier`;
            multLabel.style.cssText = `
                font-size: 10px;
                color: ${tierColor};
            `;
            multSection.appendChild(multLabel);

            const multValue = document.createElement('span');
            multValue.textContent = `×${bonusPreview.levelMultiplier.toFixed(2)} to all bonuses`;
            multValue.style.cssText = `
                font-size: 10px;
                color: ${tierColor};
            `;
            multSection.appendChild(multValue);

            modal.appendChild(multSection);
        }

        // Skill selection section
        const skillSection = document.createElement('div');
        skillSection.style.cssText = `
            background: rgba(200, 150, 255, 0.1);
            border: 1px solid rgba(200, 150, 255, 0.3);
            border-radius: 6px;
            padding: 12px;
            margin-bottom: 15px;
        `;

        const skillLabel = document.createElement('div');
        skillLabel.textContent = 'CHOOSE YOUR BONUS SKILL';
        skillLabel.style.cssText = `
            font-size: 9px;
            letter-spacing: 0.5px;
            color: rgba(200, 150, 255, 0.8);
            margin-bottom: 10px;
        `;
        skillSection.appendChild(skillLabel);

        const skillDesc = document.createElement('div');
        skillDesc.textContent = 'Mile Earning is always granted. Choose one additional skill:';
        skillDesc.style.cssText = `
            font-size: 9px;
            color: rgba(255, 255, 255, 0.5);
            margin-bottom: 10px;
        `;
        skillSection.appendChild(skillDesc);

        // Skill selection grid
        const skillGrid = document.createElement('div');
        skillGrid.style.cssText = `
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 8px;
        `;

        // Store skill button references for updating selection state
        const skillButtons = {};

        Object.values(PrestigeSystem.SELECTABLE_SKILLS).forEach(skill => {
            const skillButton = document.createElement('button');
            skillButton.dataset.skillId = skill.id;
            const currentBonus = currentSkillBonuses[skill.id] || 0;
            const isSelected = skill.id === selectedSkill;

            skillButton.style.cssText = `
                font-family: 'Courier New', monospace;
                font-size: 10px;
                padding: 8px;
                background: ${isSelected ? 'rgba(200, 150, 255, 0.3)' : 'rgba(40, 40, 55, 0.6)'};
                color: ${isSelected ? 'rgba(255, 255, 255, 0.95)' : 'rgba(255, 255, 255, 0.7)'};
                border: 1px solid ${isSelected ? 'rgba(200, 150, 255, 0.6)' : 'rgba(255, 255, 255, 0.15)'};
                border-radius: 5px;
                cursor: pointer;
                text-align: left;
                transition: all 0.15s ease;
            `;

            const skillName = document.createElement('div');
            skillName.textContent = skill.name;
            skillName.style.cssText = `
                font-weight: bold;
                margin-bottom: 2px;
            `;
            skillButton.appendChild(skillName);

            const skillInfo = document.createElement('div');
            skillInfo.style.cssText = `
                font-size: 9px;
                color: rgba(255, 255, 255, 0.5);
            `;
            if (currentBonus > 0) {
                skillInfo.textContent = `Current: ${PrestigeSystem.formatBonusPercent(currentBonus)}`;
                skillInfo.style.color = 'rgba(120, 200, 140, 0.8)';
            } else {
                skillInfo.textContent = skill.description;
            }
            skillButton.appendChild(skillInfo);

            skillButton.addEventListener('click', () => {
                selectedSkill = skill.id;
                // Update all buttons' visual state
                Object.values(skillButtons).forEach(btn => {
                    const isNowSelected = btn.dataset.skillId === selectedSkill;
                    btn.style.background = isNowSelected ? 'rgba(200, 150, 255, 0.3)' : 'rgba(40, 40, 55, 0.6)';
                    btn.style.color = isNowSelected ? 'rgba(255, 255, 255, 0.95)' : 'rgba(255, 255, 255, 0.7)';
                    btn.style.border = isNowSelected ? '1px solid rgba(200, 150, 255, 0.6)' : '1px solid rgba(255, 255, 255, 0.15)';
                });
            });

            skillButtons[skill.id] = skillButton;
            skillGrid.appendChild(skillButton);
        });

        skillSection.appendChild(skillGrid);
        modal.appendChild(skillSection);

        // Total bonus section
        const totalSection = document.createElement('div');
        totalSection.style.cssText = `
            background: rgba(120, 200, 140, 0.1);
            border: 1px solid rgba(120, 200, 140, 0.3);
            border-radius: 6px;
            padding: 12px;
            margin-bottom: 20px;
        `;

        const totalLabel = document.createElement('div');
        totalLabel.textContent = 'NEW TOTAL BONUSES';
        totalLabel.style.cssText = `
            font-size: 9px;
            letter-spacing: 0.5px;
            color: rgba(120, 200, 140, 0.8);
            margin-bottom: 8px;
        `;
        totalSection.appendChild(totalLabel);

        const totalGrid = document.createElement('div');
        totalGrid.style.cssText = `
            display: grid;
            grid-template-columns: 1fr auto;
            gap: 4px 12px;
            font-size: 11px;
        `;

        const mileLabel2 = document.createElement('span');
        mileLabel2.textContent = 'Mile Earning';
        mileLabel2.style.cssText = `color: rgba(255, 255, 255, 0.7);`;
        totalGrid.appendChild(mileLabel2);

        const mileValue2 = document.createElement('span');
        mileValue2.textContent = PrestigeSystem.formatBonusMultiplier(currentMileBonus + bonusPreview.total);
        mileValue2.style.cssText = `color: rgba(120, 200, 140, 0.95); text-align: right; font-weight: bold;`;
        totalGrid.appendChild(mileValue2);

        const skillLabel2 = document.createElement('span');
        skillLabel2.textContent = 'Selected Skill';
        skillLabel2.style.cssText = `color: rgba(255, 255, 255, 0.7);`;
        totalGrid.appendChild(skillLabel2);

        const skillValue2 = document.createElement('span');
        skillValue2.textContent = PrestigeSystem.formatBonusPercent(bonusPreview.total);
        skillValue2.style.cssText = `color: rgba(200, 150, 255, 0.95); text-align: right; font-weight: bold;`;
        totalGrid.appendChild(skillValue2);

        totalSection.appendChild(totalGrid);
        modal.appendChild(totalSection);

        // Warning section
        const warningSection = document.createElement('div');
        warningSection.style.cssText = `
            background: rgba(255, 100, 100, 0.1);
            border: 1px solid rgba(255, 100, 100, 0.3);
            border-radius: 6px;
            padding: 12px;
            margin-bottom: 20px;
            text-align: center;
        `;

        const warningText = document.createElement('div');
        warningText.textContent = '⚠ This will reset ALL progress: cars, upgrades, items, and miles.';
        warningText.style.cssText = `
            font-size: 10px;
            color: rgba(255, 150, 150, 0.9);
            line-height: 1.4;
        `;
        warningSection.appendChild(warningText);

        const keepText = document.createElement('div');
        keepText.textContent = 'Only prestige bonuses are permanent.';
        keepText.style.cssText = `
            font-size: 9px;
            color: rgba(255, 255, 255, 0.5);
            margin-top: 6px;
        `;
        warningSection.appendChild(keepText);

        modal.appendChild(warningSection);

        // Buttons
        const buttonContainer = document.createElement('div');
        buttonContainer.style.cssText = `
            display: flex;
            gap: 10px;
            justify-content: center;
        `;

        const confirmButton = document.createElement('button');
        confirmButton.textContent = `PRESTIGE → Level ${prestigeLevel + 1}`;
        confirmButton.style.cssText = `
            font-family: 'Courier New', monospace;
            font-size: 12px;
            letter-spacing: 1px;
            padding: 12px 25px;
            background: rgba(120, 80, 160, 0.9);
            color: rgba(255, 255, 255, 0.95);
            border: 1px solid rgba(200, 150, 255, 0.6);
            border-radius: 6px;
            cursor: pointer;
            transition: all 0.2s ease;
        `;
        confirmButton.addEventListener('mouseenter', () => {
            confirmButton.style.background = 'rgba(140, 100, 180, 0.95)';
        });
        confirmButton.addEventListener('mouseleave', () => {
            confirmButton.style.background = 'rgba(120, 80, 160, 0.9)';
        });
        confirmButton.addEventListener('click', () => {
            const result = this.gameState.performPrestige(selectedSkill);
            if (result) {
                modalOverlay.remove();
                parentOverlay.remove();
                // UpgradeMenuManager handles its own cleanup via UIUpdateScheduler
                // Show success message briefly, then reopen menu
                this.showPrestigeSuccessMessage(result);
            }
        });
        buttonContainer.appendChild(confirmButton);

        const cancelButton = document.createElement('button');
        cancelButton.textContent = 'CANCEL';
        cancelButton.style.cssText = `
            font-family: 'Courier New', monospace;
            font-size: 12px;
            letter-spacing: 0.5px;
            padding: 12px 20px;
            background: rgba(60, 60, 70, 0.8);
            color: rgba(255, 255, 255, 0.7);
            border: 1px solid rgba(255, 255, 255, 0.2);
            border-radius: 6px;
            cursor: pointer;
            transition: all 0.2s ease;
        `;
        cancelButton.addEventListener('mouseenter', () => {
            cancelButton.style.background = 'rgba(80, 80, 90, 0.9)';
        });
        cancelButton.addEventListener('mouseleave', () => {
            cancelButton.style.background = 'rgba(60, 60, 70, 0.8)';
        });
        cancelButton.addEventListener('click', () => {
            modalOverlay.remove();
        });
        buttonContainer.appendChild(cancelButton);

        modal.appendChild(buttonContainer);

        // Close on overlay click
        modalOverlay.addEventListener('click', (e) => {
            if (e.target === modalOverlay) {
                modalOverlay.remove();
            }
        });

        modalOverlay.appendChild(modal);
        document.body.appendChild(modalOverlay);
    }

    showPrestigeSuccessMessage(result) {
        const tierColor = PrestigeSystem.getPrestigeTierColor(result.newLevel);
        const tierName = PrestigeSystem.getPrestigeTierName(result.newLevel);

        const overlay = document.createElement('div');
        overlay.style.cssText = `
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background: rgba(0, 0, 0, 0.95);
            z-index: 400;
            display: flex;
            align-items: center;
            justify-content: center;
            pointer-events: auto;
        `;

        const message = document.createElement('div');
        message.style.cssText = `
            text-align: center;
            font-family: 'Courier New', monospace;
            animation: fadeInScale 0.5s ease-out;
        `;

        const levelText = document.createElement('div');
        levelText.textContent = `PRESTIGE LEVEL ${result.newLevel}`;
        levelText.style.cssText = `
            font-size: 24px;
            letter-spacing: 4px;
            color: ${tierColor};
            margin-bottom: 10px;
            text-shadow: 0 0 20px ${tierColor}66;
        `;
        message.appendChild(levelText);

        if (tierName) {
            const tierText = document.createElement('div');
            tierText.textContent = tierName;
            tierText.style.cssText = `
                font-size: 14px;
                letter-spacing: 2px;
                color: ${tierColor};
                margin-bottom: 25px;
                opacity: 0.8;
            `;
            message.appendChild(tierText);
        }

        const selectedSkillInfo = PrestigeSystem.SELECTABLE_SKILLS[result.selectedSkill];
        const bonusText = document.createElement('div');
        const mileLine = document.createElement('div');
        mileLine.textContent = `Mile Earning: ${PrestigeSystem.formatBonusMultiplier(result.totalMileBonus)}`;
        bonusText.appendChild(mileLine);
        const skillLine = document.createElement('div');
        skillLine.textContent = `${selectedSkillInfo ? selectedSkillInfo.name : result.selectedSkill}: ${PrestigeSystem.formatBonusPercent(result.skillBonus)}`;
        bonusText.appendChild(skillLine);
        bonusText.style.cssText = `
            font-size: 12px;
            color: rgba(120, 200, 140, 0.9);
            line-height: 1.6;
            margin-bottom: 30px;
        `;
        message.appendChild(bonusText);

        const continueText = document.createElement('div');
        continueText.textContent = 'Click anywhere to continue';
        continueText.style.cssText = `
            font-size: 10px;
            color: rgba(255, 255, 255, 0.4);
            animation: pulse 2s ease-in-out infinite;
        `;
        message.appendChild(continueText);

        overlay.appendChild(message);
        document.body.appendChild(overlay);

        overlay.addEventListener('click', () => {
            overlay.remove();
            this.showUpgradeMenu(0);
        });
    }

    showNewMilePopup(milesGained) {
        // Get position of TRIP button to spawn popup above it
        if (!this.elements.spendMilesButton) return;

        const rect = this.elements.spendMilesButton.getBoundingClientRect();
        const x = rect.left + rect.width / 2;
        const y = rect.top;

        this.popups.showMiles(x, y, milesGained);
    }

    showMaxSpeedPopup() {
        // Get position of drive button element to spawn popup above it
        if (!this.elements.driveButton) return;

        const rect = this.elements.driveButton.getBoundingClientRect();
        const x = rect.left + rect.width / 2;
        const y = rect.top;

        this.popups.showMaxSpeed(x, y);
    }

    // Register UI sounds with SFXManager
    _registerSoundsWithSFXManager() {
        if (this.sfxManager) {
            this.sfxManager.registerSound('upgrade', this.upgradeSound);
            this.sfxManager.registerSound('levelAvail', this.levelAvailSound);
            this.sfxManager.registerSound('slowdown', this.slowdownSound);

            // Also register particle system sounds
            this.particles?._registerSounds();
        }
    }

    // Trigger screen shake when reaching max speed
    triggerMaxSpeedEffects() {
        // Prevent stacking - only allow one effect at a time
        if (this.maxSpeedEffectsActive) {
            return;
        }

        if (!this.app || !this.app.camera) {
            console.warn('Cannot trigger effects - app or camera not available');
            return;
        }

        this.maxSpeedEffectsActive = true;

        const camera = this.app.camera;

        // Screen shake effect (duration: 0.5 seconds)
        const shakeDuration = 500; // ms
        const shakeIntensity = 0.15;
        const startTime = performance.now();

        const shake = () => {
            const elapsed = performance.now() - startTime;
            const progress = elapsed / shakeDuration;

            if (progress < 1) {
                // Damped shake (reduces over time)
                const damping = 1 - progress;
                const offsetX = (Math.random() - 0.5) * shakeIntensity * damping;
                const offsetY = (Math.random() - 0.5) * shakeIntensity * damping;

                camera.position.x += offsetX;
                camera.position.y += offsetY;

                requestAnimationFrame(shake);
            } else {
                // Re-enable effects after animation completes
                this.maxSpeedEffectsActive = false;
            }
        };
        shake();
    }

    // Update FOV based on current speed (called every frame)
    updateSpeedFOV() {
        if (!this.app || !this.app.camera) return;

        const currentSpeed = this.driveStateMachine.getSpeedMPH();
        const maxSpeed = this.gameState.getCurrentMaxSpeed();
        const speedPercentage = Math.min(currentSpeed / maxSpeed, 1.0); // 0.0 to 1.0

        // FOV settings
        const baseFOV = 75; // Base FOV at 0 speed
        const maxFOVIncrease = 10; // Max FOV increase at 100% speed

        // Calculate target FOV based on speed percentage
        const targetFOV = baseFOV + (speedPercentage * maxFOVIncrease);

        // Smoothly lerp to target FOV
        const lerpSpeed = 0.07; // Adjust for smoothness (lower = smoother, 1.0 = instant)
        this.app.camera.fov += (targetFOV - this.app.camera.fov) * lerpSpeed;
        this.app.camera.updateProjectionMatrix();
    }

    // Update skill available badge visibility
    updateSkillAvailBadge() {
        if (!this.elements.skillAvailBadge) return;

        const wasVisible = this.elements.skillAvailBadge.style.display === 'block';

        // Check if player can afford any upgrade
        const upgradeTypes = ['baseSpeed', 'acceleration', 'maxSpeed', 'cruiseDuration', 'cruiseCooldown', 'momentum'];
        const canAffordAny = upgradeTypes.some(type => {
            const currentCar = this.gameState.getCurrentCar();
            const upgradeInfo = this.gameState.upgrades[type];
            const currentLevel = currentCar.upgradeLevels[type] || 0;

            // Check if not at max level and can afford
            if (currentLevel < upgradeInfo.maxLevel) {
                return this.gameState.canAffordUpgrade(type);
            }
            return false;
        });

        // On first update, just record the initial state without playing effects
        if (!this.initialBadgeCheckDone) {
            this.initialBadgeCheckDone = true;
            this.elements.skillAvailBadge.style.display = canAffordAny ? 'block' : 'none';
            return;
        }

        // If badge just became visible, play sound
        if (canAffordAny && !wasVisible) {
            this.sfxManager?.playSound('levelAvail');
        }

        // Show or hide badge
        this.elements.skillAvailBadge.style.display = canAffordAny ? 'block' : 'none';
    }

    // Update pack notification badge visibility (on CAR button, WAVES tab, DASH tab, and COLLECTION tab)
    // Badge shows for UNSEEN waves, items, or collection discoveries - once viewed in respective tab, badges disappear
    updatePackNotificationBadge() {
        if (!this.elements.packNotificationBadge) return;

        // Get counts (respect suppression flags - treat suppressed content as 0 for badge purposes)
        const unseenWaveCount = this.suppressWaveBadgeUpdates ? 0 : this.gameState.getUnseenWaveCount();
        const unseenItemCount = this.suppressItemBadgeUpdates ? 0 : this.gameState.getUnseenItemCount();
        const unseenCollectionCount = this.gameState.getUnseenCollectionCount();
        const hasUnseenWaves = unseenWaveCount > 0;
        const hasUnseenItems = unseenItemCount > 0;
        const hasUnseenCollection = unseenCollectionCount > 0;
        const hasNewContent = hasUnseenWaves || hasUnseenItems || hasUnseenCollection;

        const wasVisible = this.elements.packNotificationBadge.style.display === 'block';

        // Track previous counts to detect new content (for refresh triggers)
        const prevWaveCount = this._lastUnseenWaveCount || 0;
        const prevItemCount = this._lastUnseenItemCount || 0;
        this._lastUnseenWaveCount = unseenWaveCount;
        this._lastUnseenItemCount = unseenItemCount;

        // On first update, just record the initial state without playing effects
        if (!this.initialPackBadgeCheckDone) {
            this.initialPackBadgeCheckDone = true;
            this.elements.packNotificationBadge.style.display = hasNewContent ? 'block' : 'none';
            // Also update waves tab badge (only if not suppressed)
            if (!this.suppressWaveBadgeUpdates) {
                const wavesBadge = document.getElementById('waves-badge');
                if (wavesBadge) {
                    wavesBadge.style.display = hasUnseenWaves ? 'block' : 'none';
                }
            }
            // Also update dash tab badge (only if not suppressed)
            if (!this.suppressItemBadgeUpdates) {
                const dashBadge = document.getElementById('dash-badge');
                if (dashBadge) {
                    dashBadge.style.display = hasUnseenItems ? 'block' : 'none';
                }
            }
            // Also update collection tab badge
            const collectionBadge = document.getElementById('collection-badge');
            if (collectionBadge) {
                collectionBadge.style.display = hasUnseenCollection ? 'block' : 'none';
            }
            return;
        }

        // If badge just became visible (new unseen content), play sound
        if (hasNewContent && !wasVisible) {
            this.sfxManager?.playSound('levelAvail');
        }

        // Refresh waves content in menu if open and new waves appeared (only if not viewing waves tab)
        if (!this.suppressWaveBadgeUpdates && unseenWaveCount > prevWaveCount) {
            this.upgradeMenu?.refreshWavesContent();
        }
        // Refresh inventory content in menu if open and new items appeared (only if not viewing inventory tab)
        if (!this.suppressItemBadgeUpdates && unseenItemCount > prevItemCount) {
            this.upgradeMenu?.refreshInventoryContent();
        }

        // Show or hide CAR button badge (for any new content)
        this.elements.packNotificationBadge.style.display = hasNewContent ? 'block' : 'none';

        // Show or hide waves tab badge (for unseen waves only, if not suppressed)
        if (!this.suppressWaveBadgeUpdates) {
            const wavesBadge = document.getElementById('waves-badge');
            if (wavesBadge) {
                wavesBadge.style.display = hasUnseenWaves ? 'block' : 'none';
            }
        }

        // Show or hide dash tab badge (for unseen items only, if not suppressed)
        if (!this.suppressItemBadgeUpdates) {
            const dashBadge = document.getElementById('dash-badge');
            if (dashBadge) {
                dashBadge.style.display = hasUnseenItems ? 'block' : 'none';
            }
        }

        // Show or hide collection tab badge (for unseen discoveries)
        const collectionBadge = document.getElementById('collection-badge');
        if (collectionBadge) {
            collectionBadge.style.display = hasUnseenCollection ? 'block' : 'none';
        }
    }

    playSlowdown() {
        this.sfxManager?.playSound('slowdown');
    }

    playMomentumLossAnimation() {
        if (!this.elements.momentumDisplay) return;

        // Set flag to prevent update() from hiding the display during animation
        this.momentumLossAnimationActive = true;

        // Remove pulsing animation and add fall-out animation
        this.elements.momentumDisplay.style.animation = 'momentumFallOut 0.6s ease-in forwards';

        // After animation, reset it back to pulsing and clear flags
        setTimeout(() => {
            if (this.elements.momentumDisplay) {
                this.elements.momentumDisplay.style.animation = 'momentumPulse 2s ease-in-out infinite';
                this.elements.momentumDisplay.style.display = 'none'; // Hide after animation completes
            }
            this.momentumLossAnimationActive = false;
            this.momentumDisplayWasVisible = false; // Reset so next appearance fades in
        }, 600);
    }

    /**
     * Show wave tutorial modal explaining the wave system
     * @param {function} [onContinue] - Callback when user dismisses the modal
     */
    showWaveTutorialModal(onContinue) {
        const modal = this.modals.create({
            className: 'wave-tutorial-modal',
            closeOnBackdrop: false,
            maxWidth: '460px',
            padding: '0'
        });

        // Build modal content with DOM
        const corners = document.createElement('div');
        corners.className = 'wt-corners';
        ['wt-tl', 'wt-tr', 'wt-bl', 'wt-br'].forEach(cls => {
            const corner = document.createElement('div');
            corner.className = `wt-corner ${cls}`;
            corners.appendChild(corner);
        });
        modal.container.appendChild(corners);

        const scanlines = document.createElement('div');
        scanlines.className = 'wt-scanlines';
        modal.container.appendChild(scanlines);

        const content = document.createElement('div');
        content.className = 'wt-content';

        const glyph = document.createElement('div');
        glyph.className = 'wt-glyph';
        glyph.textContent = '~';
        content.appendChild(glyph);

        const title = document.createElement('div');
        title.className = 'wt-title';
        title.textContent = 'LOT FOUND';
        content.appendChild(title);

        const divider = document.createElement('div');
        divider.className = 'wt-divider';
        content.appendChild(divider);

        const body = document.createElement('div');
        body.className = 'wt-body';
        const dimSpan = document.createElement('span');
        dimSpan.className = 'wt-dim';
        dimSpan.textContent = 'A Lot found on the road.';
        body.appendChild(dimSpan);
        body.appendChild(document.createElement('br'));
        const brightSpan = document.createElement('span');
        brightSpan.className = 'wt-bright';
        brightSpan.textContent = '3 DASH ITEMS inside. Pick one.';
        body.appendChild(brightSpan);
        content.appendChild(body);

        const meta = document.createElement('div');
        meta.className = 'wt-meta';

        const createMetaRow = (text, extraClass = '', hasHighlight = false) => {
            const row = document.createElement('div');
            row.className = `wt-meta-row${extraClass ? ' ' + extraClass : ''}`;
            const icon = document.createElement('span');
            icon.className = 'wt-meta-icon';
            icon.textContent = '>';
            row.appendChild(icon);

            if (hasHighlight) {
                const textSpan = document.createElement('span');
                textSpan.appendChild(document.createTextNode('Open from '));
                const highlight = document.createElement('span');
                highlight.className = 'wt-highlight';
                highlight.textContent = 'MI';
                textSpan.appendChild(highlight);
                textSpan.appendChild(document.createTextNode(' menu'));
                row.appendChild(textSpan);
            } else {
                const textSpan = document.createElement('span');
                textSpan.className = extraClass === 'wt-free-row' ? 'wt-free' : '';
                textSpan.textContent = text;
                row.appendChild(textSpan);
            }
            return row;
        };

        meta.appendChild(createMetaRow("You'll find more up ahead."));
        meta.appendChild(createMetaRow('', '', true)); // MI menu row
        meta.appendChild(createMetaRow("This one's free", 'wt-free-row'));
        content.appendChild(meta);

        const btn = document.createElement('button');
        btn.className = 'wt-btn';
        const btnText = document.createElement('span');
        btnText.className = 'wt-btn-text';
        btnText.textContent = 'COOL';
        btn.appendChild(btnText);
        const btnGlow = document.createElement('span');
        btnGlow.className = 'wt-btn-glow';
        btn.appendChild(btnGlow);
        content.appendChild(btn);

        modal.container.appendChild(content);

        // Add styles
        const style = document.createElement('style');
        style.textContent = `
            .wave-tutorial-modal .gameui-modal-container {
                background: linear-gradient(180deg, rgba(15, 18, 25, 0.98) 0%, rgba(10, 12, 18, 0.99) 100%);
                border: 1px solid rgba(100, 200, 255, 0.15);
                border-radius: 0;
                padding: 0;
                text-align: center;
                max-width: 340px;
                position: relative;
                overflow: hidden;
                box-shadow:
                    0 0 40px rgba(100, 180, 255, 0.08),
                    inset 0 1px 0 rgba(255, 255, 255, 0.05);
                animation: wt-appear 0.3s ease-out;
            }

            @keyframes wt-appear {
                from { opacity: 0; transform: scale(0.95) translateY(-10px); }
                to { opacity: 1; transform: scale(1) translateY(0); }
            }

            .wt-corners { position: absolute; inset: 0; pointer-events: none; }
            .wt-corner {
                position: absolute;
                width: 12px;
                height: 12px;
                border-color: rgba(100, 200, 255, 0.4);
                border-style: solid;
                border-width: 0;
            }
            .wt-tl { top: 8px; left: 8px; border-top-width: 1px; border-left-width: 1px; }
            .wt-tr { top: 8px; right: 8px; border-top-width: 1px; border-right-width: 1px; }
            .wt-bl { bottom: 8px; left: 8px; border-bottom-width: 1px; border-left-width: 1px; }
            .wt-br { bottom: 8px; right: 8px; border-bottom-width: 1px; border-right-width: 1px; }

            .wt-scanlines {
                position: absolute;
                inset: 0;
                background: repeating-linear-gradient(
                    0deg,
                    transparent,
                    transparent 2px,
                    rgba(0, 0, 0, 0.1) 2px,
                    rgba(0, 0, 0, 0.1) 4px
                );
                pointer-events: none;
                opacity: 0.3;
            }

            .wt-content {
                position: relative;
                padding: 32px 28px 28px;
            }

            .wt-glyph {
                font-size: 52px;
                color: rgba(100, 200, 255, 0.7);
                margin-bottom: 2px;
                font-family: 'IBM Plex Mono', monospace;
                text-shadow: 0 0 30px rgba(100, 200, 255, 0.5);
                animation: wt-pulse 3s ease-in-out infinite;
            }

            @keyframes wt-pulse {
                0%, 100% { opacity: 0.7; text-shadow: 0 0 30px rgba(100, 200, 255, 0.5); }
                50% { opacity: 1; text-shadow: 0 0 40px rgba(100, 200, 255, 0.8); }
            }

            .wt-title {
                font-family: 'IBM Plex Mono', monospace;
                font-size: 14px;
                font-weight: 600;
                color: rgba(255, 255, 255, 0.95);
                letter-spacing: 6px;
                margin-bottom: 16px;
            }

            .wt-divider {
                width: 40px;
                height: 1px;
                background: linear-gradient(90deg, transparent, rgba(100, 200, 255, 0.5), transparent);
                margin: 0 auto 20px;
            }

            .wt-body {
                font-family: 'IBM Plex Mono', monospace;
                font-size: 13px;
                line-height: 1.8;
                margin-bottom: 24px;
            }

            .wt-dim {
                color: rgba(255, 255, 255, 0.4);
            }

            .wt-bright {
                color: rgba(255, 255, 255, 0.9);
            }

            .wt-meta {
                font-family: 'IBM Plex Mono', monospace;
                font-size: 11px;
                color: rgba(255, 255, 255, 0.35);
                margin-bottom: 28px;
                text-align: left;
                padding: 0 20px;
            }

            .wt-meta-row {
                display: flex;
                align-items: center;
                gap: 8px;
                margin-bottom: 6px;
            }

            .wt-meta-icon {
                color: rgba(100, 200, 255, 0.5);
                font-size: 10px;
            }

            .wt-highlight {
                color: rgba(100, 200, 255, 0.8);
            }

            .wt-free {
                color: rgba(100, 220, 150, 0.9);
            }

            .wt-free-row .wt-meta-icon {
                color: rgba(100, 220, 150, 0.6);
            }

            .wt-btn {
                font-family: 'IBM Plex Mono', monospace;
                font-size: 12px;
                font-weight: 600;
                padding: 12px 44px;
                background: rgba(100, 200, 255, 0.1);
                color: rgba(100, 200, 255, 0.9);
                border: 1px solid rgba(100, 200, 255, 0.3);
                cursor: pointer;
                transition: all 0.2s ease;
                letter-spacing: 3px;
                position: relative;
                overflow: hidden;
            }

            .wt-btn-glow {
                position: absolute;
                inset: 0;
                background: linear-gradient(90deg, transparent, rgba(100, 200, 255, 0.2), transparent);
                transform: translateX(-100%);
                transition: transform 0.4s ease;
            }

            .wt-btn:hover {
                background: rgba(100, 200, 255, 0.2);
                border-color: rgba(100, 200, 255, 0.5);
                color: rgba(255, 255, 255, 1);
                box-shadow: 0 0 20px rgba(100, 200, 255, 0.3);
            }

            .wt-btn:hover .wt-btn-glow {
                transform: translateX(100%);
            }
        `;
        modal.container.appendChild(style);

        // Handle continue button
        modal.container.querySelector('.wt-btn').addEventListener('click', () => {
            // Mark tutorial as shown
            if (this.gameState.state.waves) {
                this.gameState.state.waves.tutorialShown = true;
                this.gameState.save();
            }
            modal.close();
            if (onContinue) onContinue();
        });

        // Also allow Enter key to continue
        const handleKeydown = (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                if (this.gameState.state.waves) {
                    this.gameState.state.waves.tutorialShown = true;
                    this.gameState.save();
                }
                modal.close();
                document.removeEventListener('keydown', handleKeydown);
                if (onContinue) onContinue();
            }
        };
        document.addEventListener('keydown', handleKeydown);
    }

    /**
     * Show save conflict modal - lets user choose between local and cloud saves
     * @param {object} options - Conflict options
     * @param {object} options.localState - Local save state
     * @param {object} options.cloudState - Cloud save state
     * @param {number} options.cloudTimestamp - Cloud save timestamp (ms)
     * @param {function} options.onChooseLocal - Callback when user picks local
     * @param {function} options.onChooseCloud - Callback when user picks cloud
     */
    showSaveConflictModal({ localState, cloudState, cloudTimestamp, onChooseLocal, onChooseCloud }) {
        const modal = this.modals.create({
            className: 'save-conflict-modal',
            closeOnBackdrop: false,
            maxWidth: '420px',
            padding: '0'
        });

        // Format timestamp to relative time
        const formatTime = (timestamp) => {
            if (!timestamp) return 'Unknown';
            const now = Date.now();
            const diff = now - timestamp;
            const mins = Math.floor(diff / 60000);
            const hours = Math.floor(diff / 3600000);
            const days = Math.floor(diff / 86400000);

            if (mins < 1) return 'Just now';
            if (mins < 60) return `${mins}m ago`;
            if (hours < 24) return `${hours}h ago`;
            if (days < 7) return `${days}d ago`;
            return new Date(timestamp).toLocaleDateString();
        };

        const localMiles = Math.floor(localState?.lifetimeMiles ?? localState?.totalMiles ?? 0);
        const cloudMiles = Math.floor(cloudState?.lifetimeMiles ?? cloudState?.totalMiles ?? 0);
        const localTime = localState?._lastSaveTime || Date.now();
        const cloudTime = cloudTimestamp || 0;

        // Build modal content with DOM
        const createCorners = (prefix) => {
            const corners = document.createElement('div');
            corners.className = `${prefix}-corners`;
            [`${prefix}-tl`, `${prefix}-tr`, `${prefix}-bl`, `${prefix}-br`].forEach(cls => {
                const corner = document.createElement('div');
                corner.className = `${prefix}-corner ${cls}`;
                corners.appendChild(corner);
            });
            return corners;
        };

        const createOptionButton = (label, miles, time, extraClass) => {
            const btn = document.createElement('button');
            btn.className = `sc-option ${extraClass}`;

            const header = document.createElement('div');
            header.className = 'sc-option-header';
            const icon = document.createElement('span');
            icon.className = 'sc-option-icon';
            icon.textContent = '>';
            header.appendChild(icon);
            const labelSpan = document.createElement('span');
            labelSpan.className = 'sc-option-label';
            labelSpan.textContent = label;
            header.appendChild(labelSpan);
            btn.appendChild(header);

            const stats = document.createElement('div');
            stats.className = 'sc-option-stats';

            const milesStat = document.createElement('div');
            milesStat.className = 'sc-stat';
            const milesValue = document.createElement('span');
            milesValue.className = 'sc-stat-value';
            milesValue.textContent = miles.toLocaleString();
            milesStat.appendChild(milesValue);
            const milesLabel = document.createElement('span');
            milesLabel.className = 'sc-stat-label';
            milesLabel.textContent = 'lifetime miles';
            milesStat.appendChild(milesLabel);
            stats.appendChild(milesStat);

            const timeStat = document.createElement('div');
            timeStat.className = 'sc-stat';
            const timeValue = document.createElement('span');
            timeValue.className = 'sc-stat-value sc-time';
            timeValue.textContent = time;
            timeStat.appendChild(timeValue);
            const timeLabel = document.createElement('span');
            timeLabel.className = 'sc-stat-label';
            timeLabel.textContent = 'saved';
            timeStat.appendChild(timeLabel);
            stats.appendChild(timeStat);

            btn.appendChild(stats);
            return btn;
        };

        modal.container.appendChild(createCorners('sc'));

        const content = document.createElement('div');
        content.className = 'sc-content';

        const iconEl = document.createElement('div');
        iconEl.className = 'sc-icon';
        iconEl.textContent = '!';
        content.appendChild(iconEl);

        const titleEl = document.createElement('div');
        titleEl.className = 'sc-title';
        titleEl.textContent = 'SAVE CONFLICT';
        content.appendChild(titleEl);

        const subtitle = document.createElement('div');
        subtitle.className = 'sc-subtitle';
        subtitle.textContent = 'Which save would you like to keep?';
        content.appendChild(subtitle);

        const options = document.createElement('div');
        options.className = 'sc-options';
        options.appendChild(createOptionButton('THIS DEVICE', localMiles, formatTime(localTime), 'sc-local'));
        options.appendChild(createOptionButton('CLOUD', cloudMiles, formatTime(cloudTime), 'sc-cloud'));
        content.appendChild(options);

        const warning = document.createElement('div');
        warning.className = 'sc-warning';
        warning.textContent = 'The other save will be overwritten';
        content.appendChild(warning);

        modal.container.appendChild(content);

        // Add styles
        const style = document.createElement('style');
        style.textContent = `
            .save-conflict-modal .gameui-modal-container {
                background: linear-gradient(180deg, rgba(20, 15, 18, 0.98) 0%, rgba(15, 12, 15, 0.99) 100%);
                border: 1px solid rgba(255, 180, 100, 0.2);
                border-radius: 0;
                padding: 0;
                position: relative;
                overflow: hidden;
                box-shadow: 0 0 40px rgba(255, 150, 50, 0.1);
                animation: sc-appear 0.3s ease-out;
            }

            @keyframes sc-appear {
                from { opacity: 0; transform: scale(0.95); }
                to { opacity: 1; transform: scale(1); }
            }

            .sc-corners { position: absolute; inset: 0; pointer-events: none; }
            .sc-corner {
                position: absolute;
                width: 12px;
                height: 12px;
                border-color: rgba(255, 180, 100, 0.4);
                border-style: solid;
                border-width: 0;
            }
            .sc-tl { top: 8px; left: 8px; border-top-width: 1px; border-left-width: 1px; }
            .sc-tr { top: 8px; right: 8px; border-top-width: 1px; border-right-width: 1px; }
            .sc-bl { bottom: 8px; left: 8px; border-bottom-width: 1px; border-left-width: 1px; }
            .sc-br { bottom: 8px; right: 8px; border-bottom-width: 1px; border-right-width: 1px; }

            .sc-content {
                position: relative;
                padding: 28px 24px 24px;
                text-align: center;
            }

            .sc-icon {
                font-size: 32px;
                color: rgba(255, 180, 100, 0.8);
                margin-bottom: 8px;
                font-family: 'IBM Plex Mono', monospace;
                font-weight: bold;
            }

            .sc-title {
                font-family: 'IBM Plex Mono', monospace;
                font-size: 14px;
                font-weight: 600;
                color: rgba(255, 255, 255, 0.95);
                letter-spacing: 4px;
                margin-bottom: 8px;
            }

            .sc-subtitle {
                font-family: 'IBM Plex Mono', monospace;
                font-size: 12px;
                color: rgba(255, 255, 255, 0.5);
                margin-bottom: 24px;
            }

            .sc-options {
                display: flex;
                flex-direction: column;
                gap: 12px;
                margin-bottom: 20px;
            }

            .sc-option {
                background: rgba(255, 255, 255, 0.03);
                border: 1px solid rgba(255, 255, 255, 0.1);
                padding: 16px;
                cursor: pointer;
                transition: all 0.2s ease;
                text-align: left;
                font-family: 'IBM Plex Mono', monospace;
            }

            .sc-option:hover {
                background: rgba(255, 255, 255, 0.06);
                border-color: rgba(255, 255, 255, 0.2);
            }

            .sc-option:active {
                transform: scale(0.98);
            }

            .sc-local:hover {
                border-color: rgba(100, 200, 255, 0.4);
                box-shadow: 0 0 20px rgba(100, 200, 255, 0.1);
            }

            .sc-cloud:hover {
                border-color: rgba(100, 220, 150, 0.4);
                box-shadow: 0 0 20px rgba(100, 220, 150, 0.1);
            }

            .sc-option-header {
                display: flex;
                align-items: center;
                gap: 8px;
                margin-bottom: 12px;
            }

            .sc-option-icon {
                color: rgba(255, 255, 255, 0.4);
                font-size: 12px;
            }

            .sc-local:hover .sc-option-icon { color: rgba(100, 200, 255, 0.8); }
            .sc-cloud:hover .sc-option-icon { color: rgba(100, 220, 150, 0.8); }

            .sc-option-label {
                font-size: 11px;
                font-weight: 600;
                letter-spacing: 2px;
                color: rgba(255, 255, 255, 0.7);
            }

            .sc-local:hover .sc-option-label { color: rgba(100, 200, 255, 0.9); }
            .sc-cloud:hover .sc-option-label { color: rgba(100, 220, 150, 0.9); }

            .sc-option-stats {
                display: flex;
                justify-content: space-between;
            }

            .sc-stat {
                display: flex;
                flex-direction: column;
                gap: 2px;
            }

            .sc-stat-value {
                font-size: 16px;
                font-weight: 500;
                color: rgba(255, 255, 255, 0.9);
            }

            .sc-stat-value.sc-time {
                font-size: 13px;
                color: rgba(255, 255, 255, 0.6);
            }

            .sc-stat-label {
                font-size: 10px;
                color: rgba(255, 255, 255, 0.4);
                letter-spacing: 0.5px;
            }

            .sc-warning {
                font-family: 'IBM Plex Mono', monospace;
                font-size: 10px;
                color: rgba(255, 180, 100, 0.6);
                letter-spacing: 0.5px;
            }
        `;
        modal.container.appendChild(style);

        // Handle button clicks - show confirmation before proceeding
        modal.container.querySelector('.sc-local').addEventListener('click', () => {
            this.showSaveConfirmModal({
                choice: 'THIS DEVICE',
                warningText: 'Cloud save will be overwritten',
                onConfirm: () => {
                    modal.close();
                    if (onChooseLocal) onChooseLocal();
                },
                onCancel: () => {
                    // Do nothing - user stays on conflict modal
                }
            });
        });

        modal.container.querySelector('.sc-cloud').addEventListener('click', () => {
            this.showSaveConfirmModal({
                choice: 'CLOUD',
                warningText: 'Local save will be overwritten',
                onConfirm: () => {
                    modal.close();
                    if (onChooseCloud) onChooseCloud();
                },
                onCancel: () => {
                    // Do nothing - user stays on conflict modal
                }
            });
        });
    }

    /**
     * Show confirmation modal for save conflict choice
     * @param {object} options - Confirmation options
     * @param {string} options.choice - The choice being confirmed ('THIS DEVICE' or 'CLOUD')
     * @param {string} options.warningText - Warning about what will be lost
     * @param {function} options.onConfirm - Callback when user confirms
     * @param {function} options.onCancel - Callback when user cancels
     */
    showSaveConfirmModal({ choice, warningText, onConfirm, onCancel }) {
        const modal = this.modals.create({
            className: 'save-confirm-modal',
            closeOnBackdrop: false,
            maxWidth: '360px',
            padding: '0'
        });

        // Build modal content with DOM
        const createCorners = (prefix) => {
            const corners = document.createElement('div');
            corners.className = `${prefix}-corners`;
            [`${prefix}-tl`, `${prefix}-tr`, `${prefix}-bl`, `${prefix}-br`].forEach(cls => {
                const corner = document.createElement('div');
                corner.className = `${prefix}-corner ${cls}`;
                corners.appendChild(corner);
            });
            return corners;
        };

        modal.container.appendChild(createCorners('scf'));

        const content = document.createElement('div');
        content.className = 'scf-content';

        const iconEl = document.createElement('div');
        iconEl.className = 'scf-icon';
        iconEl.textContent = '?';
        content.appendChild(iconEl);

        const titleEl = document.createElement('div');
        titleEl.className = 'scf-title';
        titleEl.textContent = 'ARE YOU SURE?';
        content.appendChild(titleEl);

        const choiceEl = document.createElement('div');
        choiceEl.className = 'scf-choice';
        choiceEl.appendChild(document.createTextNode('Use '));
        const choiceName = document.createElement('span');
        choiceName.className = 'scf-choice-name';
        choiceName.textContent = choice;
        choiceEl.appendChild(choiceName);
        choiceEl.appendChild(document.createTextNode(' save'));
        content.appendChild(choiceEl);

        const warningEl = document.createElement('div');
        warningEl.className = 'scf-warning';
        warningEl.textContent = warningText;
        content.appendChild(warningEl);

        const buttons = document.createElement('div');
        buttons.className = 'scf-buttons';
        const cancelBtn = document.createElement('button');
        cancelBtn.className = 'scf-btn scf-cancel';
        cancelBtn.textContent = 'CANCEL';
        buttons.appendChild(cancelBtn);
        const confirmBtn = document.createElement('button');
        confirmBtn.className = 'scf-btn scf-confirm';
        confirmBtn.textContent = 'CONFIRM';
        buttons.appendChild(confirmBtn);
        content.appendChild(buttons);

        modal.container.appendChild(content);

        // Add styles
        const style = document.createElement('style');
        style.textContent = `
            .save-confirm-modal .gameui-modal-container {
                background: linear-gradient(180deg, rgba(20, 15, 18, 0.98) 0%, rgba(15, 12, 15, 0.99) 100%);
                border: 1px solid rgba(255, 100, 100, 0.3);
                border-radius: 0;
                padding: 0;
                position: relative;
                overflow: hidden;
                box-shadow: 0 0 40px rgba(255, 100, 100, 0.15);
                animation: scf-appear 0.2s ease-out;
            }

            @keyframes scf-appear {
                from { opacity: 0; transform: scale(0.95); }
                to { opacity: 1; transform: scale(1); }
            }

            .scf-corners { position: absolute; inset: 0; pointer-events: none; }
            .scf-corner {
                position: absolute;
                width: 10px;
                height: 10px;
                border-color: rgba(255, 100, 100, 0.5);
                border-style: solid;
                border-width: 0;
            }
            .scf-tl { top: 6px; left: 6px; border-top-width: 1px; border-left-width: 1px; }
            .scf-tr { top: 6px; right: 6px; border-top-width: 1px; border-right-width: 1px; }
            .scf-bl { bottom: 6px; left: 6px; border-bottom-width: 1px; border-left-width: 1px; }
            .scf-br { bottom: 6px; right: 6px; border-bottom-width: 1px; border-right-width: 1px; }

            .scf-content {
                position: relative;
                padding: 24px 20px 20px;
                text-align: center;
            }

            .scf-icon {
                font-size: 28px;
                color: rgba(255, 100, 100, 0.8);
                margin-bottom: 8px;
                font-family: 'IBM Plex Mono', monospace;
                font-weight: bold;
            }

            .scf-title {
                font-family: 'IBM Plex Mono', monospace;
                font-size: 13px;
                font-weight: 600;
                color: rgba(255, 255, 255, 0.95);
                letter-spacing: 3px;
                margin-bottom: 12px;
            }

            .scf-choice {
                font-family: 'IBM Plex Mono', monospace;
                font-size: 12px;
                color: rgba(255, 255, 255, 0.7);
                margin-bottom: 6px;
            }

            .scf-choice-name {
                color: rgba(255, 255, 255, 0.95);
                font-weight: 600;
            }

            .scf-warning {
                font-family: 'IBM Plex Mono', monospace;
                font-size: 10px;
                color: rgba(255, 100, 100, 0.7);
                margin-bottom: 20px;
                letter-spacing: 0.5px;
            }

            .scf-buttons {
                display: flex;
                gap: 12px;
                justify-content: center;
            }

            .scf-btn {
                font-family: 'IBM Plex Mono', monospace;
                font-size: 11px;
                font-weight: 600;
                letter-spacing: 2px;
                padding: 10px 20px;
                border: 1px solid rgba(255, 255, 255, 0.2);
                background: rgba(255, 255, 255, 0.03);
                color: rgba(255, 255, 255, 0.7);
                cursor: pointer;
                transition: all 0.2s ease;
            }

            .scf-btn:hover {
                background: rgba(255, 255, 255, 0.08);
                border-color: rgba(255, 255, 255, 0.3);
                color: rgba(255, 255, 255, 0.9);
            }

            .scf-cancel:hover {
                border-color: rgba(255, 255, 255, 0.4);
            }

            .scf-confirm {
                border-color: rgba(255, 100, 100, 0.4);
                color: rgba(255, 150, 150, 0.9);
            }

            .scf-confirm:hover {
                background: rgba(255, 100, 100, 0.15);
                border-color: rgba(255, 100, 100, 0.6);
                color: rgba(255, 200, 200, 1);
                box-shadow: 0 0 15px rgba(255, 100, 100, 0.2);
            }

            .scf-btn:active {
                transform: scale(0.97);
            }
        `;
        modal.container.appendChild(style);

        // Handle button clicks
        modal.container.querySelector('.scf-cancel').addEventListener('click', () => {
            modal.close();
            if (onCancel) onCancel();
        });

        modal.container.querySelector('.scf-confirm').addEventListener('click', () => {
            modal.close();
            if (onConfirm) onConfirm();
        });
    }

    /**
     * Cleanup resources
     * Unregisters scheduled callbacks from UIUpdateScheduler (Phase 6 TMR-03)
     */
    dispose() {
        if (this._friendBadgeUpdateId) {
            const scheduler = getUIUpdateScheduler();
            scheduler.unregister(this._friendBadgeUpdateId);
        }
    }
}
